/**
 * Proton Browser Transport v4.2.18
 * https://github.com/protonprotocol/proton-browser-transport
 *
 * @license
 * Copyright (c) 2020 Greymass Inc. All Rights Reserved.
 * 
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 * 
 *  1. Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 * 
 *  2. Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 * 
 *  3. Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 * 
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR INTENDED FOR USE
 * IN THE DESIGN, CONSTRUCTION, OPERATION OR MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { SessionError, Base64u } from '@proton/link';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function noop() { }
function assign(tar, src) {
    // @ts-ignore
    for (const k in src)
        tar[k] = src[k];
    return tar;
}
function add_location(element, file, line, column, char) {
    element.__svelte_meta = {
        loc: { file, line, column, char }
    };
}
function run(fn) {
    return fn();
}
function blank_object() {
    return Object.create(null);
}
function run_all(fns) {
    fns.forEach(run);
}
function is_function(thing) {
    return typeof thing === 'function';
}
function safe_not_equal(a, b) {
    return a != a ? b == b : a !== b || ((a && typeof a === 'object') || typeof a === 'function');
}
let src_url_equal_anchor;
function src_url_equal(element_src, url) {
    if (!src_url_equal_anchor) {
        src_url_equal_anchor = document.createElement('a');
    }
    src_url_equal_anchor.href = url;
    return element_src === src_url_equal_anchor.href;
}
function is_empty(obj) {
    return Object.keys(obj).length === 0;
}
function append(target, node) {
    target.appendChild(node);
}
function append_styles(target, style_sheet_id, styles) {
    const append_styles_to = get_root_for_style(target);
    if (!append_styles_to.getElementById(style_sheet_id)) {
        const style = element('style');
        style.id = style_sheet_id;
        style.textContent = styles;
        append_stylesheet(append_styles_to, style);
    }
}
function get_root_for_style(node) {
    if (!node)
        return document;
    const root = node.getRootNode ? node.getRootNode() : node.ownerDocument;
    if (root && root.host) {
        return root;
    }
    return node.ownerDocument;
}
function append_stylesheet(node, style) {
    append(node.head || node, style);
}
function insert(target, node, anchor) {
    target.insertBefore(node, anchor || null);
}
function detach(node) {
    node.parentNode.removeChild(node);
}
function element(name) {
    return document.createElement(name);
}
function text(data) {
    return document.createTextNode(data);
}
function space() {
    return text(' ');
}
function listen(node, event, handler, options) {
    node.addEventListener(event, handler, options);
    return () => node.removeEventListener(event, handler, options);
}
function prevent_default(fn) {
    return function (event) {
        event.preventDefault();
        // @ts-ignore
        return fn.call(this, event);
    };
}
function stop_propagation(fn) {
    return function (event) {
        event.stopPropagation();
        // @ts-ignore
        return fn.call(this, event);
    };
}
function self(fn) {
    return function (event) {
        // @ts-ignore
        if (event.target === this)
            fn.call(this, event);
    };
}
function attr(node, attribute, value) {
    if (value == null)
        node.removeAttribute(attribute);
    else if (node.getAttribute(attribute) !== value)
        node.setAttribute(attribute, value);
}
function children(element) {
    return Array.from(element.childNodes);
}
function toggle_class(element, name, toggle) {
    element.classList[toggle ? 'add' : 'remove'](name);
}
function custom_event(type, detail, { bubbles = false, cancelable = false } = {}) {
    const e = document.createEvent('CustomEvent');
    e.initCustomEvent(type, bubbles, cancelable, detail);
    return e;
}

let current_component;
function set_current_component(component) {
    current_component = component;
}
function get_current_component() {
    if (!current_component)
        throw new Error('Function called outside component initialization');
    return current_component;
}
function createEventDispatcher() {
    const component = get_current_component();
    return (type, detail, { cancelable = false } = {}) => {
        const callbacks = component.$$.callbacks[type];
        if (callbacks) {
            // TODO are there situations where events could be dispatched
            // in a server (non-DOM) environment?
            const event = custom_event(type, detail, { cancelable });
            callbacks.slice().forEach(fn => {
                fn.call(component, event);
            });
            return !event.defaultPrevented;
        }
        return true;
    };
}

const dirty_components = [];
const binding_callbacks = [];
const render_callbacks = [];
const flush_callbacks = [];
const resolved_promise = Promise.resolve();
let update_scheduled = false;
function schedule_update() {
    if (!update_scheduled) {
        update_scheduled = true;
        resolved_promise.then(flush);
    }
}
function add_render_callback(fn) {
    render_callbacks.push(fn);
}
// flush() calls callbacks in this order:
// 1. All beforeUpdate callbacks, in order: parents before children
// 2. All bind:this callbacks, in reverse order: children before parents.
// 3. All afterUpdate callbacks, in order: parents before children. EXCEPT
//    for afterUpdates called during the initial onMount, which are called in
//    reverse order: children before parents.
// Since callbacks might update component values, which could trigger another
// call to flush(), the following steps guard against this:
// 1. During beforeUpdate, any updated components will be added to the
//    dirty_components array and will cause a reentrant call to flush(). Because
//    the flush index is kept outside the function, the reentrant call will pick
//    up where the earlier call left off and go through all dirty components. The
//    current_component value is saved and restored so that the reentrant call will
//    not interfere with the "parent" flush() call.
// 2. bind:this callbacks cannot trigger new flush() calls.
// 3. During afterUpdate, any updated components will NOT have their afterUpdate
//    callback called a second time; the seen_callbacks set, outside the flush()
//    function, guarantees this behavior.
const seen_callbacks = new Set();
let flushidx = 0; // Do *not* move this inside the flush() function
function flush() {
    const saved_component = current_component;
    do {
        // first, call beforeUpdate functions
        // and update components
        while (flushidx < dirty_components.length) {
            const component = dirty_components[flushidx];
            flushidx++;
            set_current_component(component);
            update(component.$$);
        }
        set_current_component(null);
        dirty_components.length = 0;
        flushidx = 0;
        while (binding_callbacks.length)
            binding_callbacks.pop()();
        // then, once components are updated, call
        // afterUpdate functions. This may cause
        // subsequent updates...
        for (let i = 0; i < render_callbacks.length; i += 1) {
            const callback = render_callbacks[i];
            if (!seen_callbacks.has(callback)) {
                // ...so guard against infinite loops
                seen_callbacks.add(callback);
                callback();
            }
        }
        render_callbacks.length = 0;
    } while (dirty_components.length);
    while (flush_callbacks.length) {
        flush_callbacks.pop()();
    }
    update_scheduled = false;
    seen_callbacks.clear();
    set_current_component(saved_component);
}
function update($$) {
    if ($$.fragment !== null) {
        $$.update();
        run_all($$.before_update);
        const dirty = $$.dirty;
        $$.dirty = [-1];
        $$.fragment && $$.fragment.p($$.ctx, dirty);
        $$.after_update.forEach(add_render_callback);
    }
}
const outroing = new Set();
let outros;
function group_outros() {
    outros = {
        r: 0,
        c: [],
        p: outros // parent group
    };
}
function check_outros() {
    if (!outros.r) {
        run_all(outros.c);
    }
    outros = outros.p;
}
function transition_in(block, local) {
    if (block && block.i) {
        outroing.delete(block);
        block.i(local);
    }
}
function transition_out(block, local, detach, callback) {
    if (block && block.o) {
        if (outroing.has(block))
            return;
        outroing.add(block);
        outros.c.push(() => {
            outroing.delete(block);
            if (callback) {
                if (detach)
                    block.d(1);
                callback();
            }
        });
        block.o(local);
    }
    else if (callback) {
        callback();
    }
}

function get_spread_update(levels, updates) {
    const update = {};
    const to_null_out = {};
    const accounted_for = { $$scope: 1 };
    let i = levels.length;
    while (i--) {
        const o = levels[i];
        const n = updates[i];
        if (n) {
            for (const key in o) {
                if (!(key in n))
                    to_null_out[key] = 1;
            }
            for (const key in n) {
                if (!accounted_for[key]) {
                    update[key] = n[key];
                    accounted_for[key] = 1;
                }
            }
            levels[i] = n;
        }
        else {
            for (const key in o) {
                accounted_for[key] = 1;
            }
        }
    }
    for (const key in to_null_out) {
        if (!(key in update))
            update[key] = undefined;
    }
    return update;
}
function get_spread_object(spread_props) {
    return typeof spread_props === 'object' && spread_props !== null ? spread_props : {};
}
function create_component(block) {
    block && block.c();
}
function mount_component(component, target, anchor, customElement) {
    const { fragment, on_mount, on_destroy, after_update } = component.$$;
    fragment && fragment.m(target, anchor);
    if (!customElement) {
        // onMount happens before the initial afterUpdate
        add_render_callback(() => {
            const new_on_destroy = on_mount.map(run).filter(is_function);
            if (on_destroy) {
                on_destroy.push(...new_on_destroy);
            }
            else {
                // Edge case - component was destroyed immediately,
                // most likely as a result of a binding initialising
                run_all(new_on_destroy);
            }
            component.$$.on_mount = [];
        });
    }
    after_update.forEach(add_render_callback);
}
function destroy_component(component, detaching) {
    const $$ = component.$$;
    if ($$.fragment !== null) {
        run_all($$.on_destroy);
        $$.fragment && $$.fragment.d(detaching);
        // TODO null out other refs, including component.$$ (but need to
        // preserve final state?)
        $$.on_destroy = $$.fragment = null;
        $$.ctx = [];
    }
}
function make_dirty(component, i) {
    if (component.$$.dirty[0] === -1) {
        dirty_components.push(component);
        schedule_update();
        component.$$.dirty.fill(0);
    }
    component.$$.dirty[(i / 31) | 0] |= (1 << (i % 31));
}
function init(component, options, instance, create_fragment, not_equal, props, append_styles, dirty = [-1]) {
    const parent_component = current_component;
    set_current_component(component);
    const $$ = component.$$ = {
        fragment: null,
        ctx: null,
        // state
        props,
        update: noop,
        not_equal,
        bound: blank_object(),
        // lifecycle
        on_mount: [],
        on_destroy: [],
        on_disconnect: [],
        before_update: [],
        after_update: [],
        context: new Map(options.context || (parent_component ? parent_component.$$.context : [])),
        // everything else
        callbacks: blank_object(),
        dirty,
        skip_bound: false,
        root: options.target || parent_component.$$.root
    };
    append_styles && append_styles($$.root);
    let ready = false;
    $$.ctx = instance
        ? instance(component, options.props || {}, (i, ret, ...rest) => {
            const value = rest.length ? rest[0] : ret;
            if ($$.ctx && not_equal($$.ctx[i], $$.ctx[i] = value)) {
                if (!$$.skip_bound && $$.bound[i])
                    $$.bound[i](value);
                if (ready)
                    make_dirty(component, i);
            }
            return ret;
        })
        : [];
    $$.update();
    ready = true;
    run_all($$.before_update);
    // `false` as a special case of no DOM component
    $$.fragment = create_fragment ? create_fragment($$.ctx) : false;
    if (options.target) {
        if (options.hydrate) {
            const nodes = children(options.target);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            $$.fragment && $$.fragment.l(nodes);
            nodes.forEach(detach);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            $$.fragment && $$.fragment.c();
        }
        if (options.intro)
            transition_in(component.$$.fragment);
        mount_component(component, options.target, options.anchor, options.customElement);
        flush();
    }
    set_current_component(parent_component);
}
/**
 * Base class for Svelte components. Used when dev=false.
 */
class SvelteComponent {
    $destroy() {
        destroy_component(this, 1);
        this.$destroy = noop;
    }
    $on(type, callback) {
        const callbacks = (this.$$.callbacks[type] || (this.$$.callbacks[type] = []));
        callbacks.push(callback);
        return () => {
            const index = callbacks.indexOf(callback);
            if (index !== -1)
                callbacks.splice(index, 1);
        };
    }
    $set($$props) {
        if (this.$$set && !is_empty($$props)) {
            this.$$.skip_bound = true;
            this.$$set($$props);
            this.$$.skip_bound = false;
        }
    }
}

function dispatch_dev(type, detail) {
    document.dispatchEvent(custom_event(type, Object.assign({ version: '3.49.0' }, detail), { bubbles: true }));
}
function append_dev(target, node) {
    dispatch_dev('SvelteDOMInsert', { target, node });
    append(target, node);
}
function insert_dev(target, node, anchor) {
    dispatch_dev('SvelteDOMInsert', { target, node, anchor });
    insert(target, node, anchor);
}
function detach_dev(node) {
    dispatch_dev('SvelteDOMRemove', { node });
    detach(node);
}
function listen_dev(node, event, handler, options, has_prevent_default, has_stop_propagation) {
    const modifiers = options === true ? ['capture'] : options ? Array.from(Object.keys(options)) : [];
    if (has_prevent_default)
        modifiers.push('preventDefault');
    if (has_stop_propagation)
        modifiers.push('stopPropagation');
    dispatch_dev('SvelteDOMAddEventListener', { node, event, handler, modifiers });
    const dispose = listen(node, event, handler, options);
    return () => {
        dispatch_dev('SvelteDOMRemoveEventListener', { node, event, handler, modifiers });
        dispose();
    };
}
function attr_dev(node, attribute, value) {
    attr(node, attribute, value);
    if (value == null)
        dispatch_dev('SvelteDOMRemoveAttribute', { node, attribute });
    else
        dispatch_dev('SvelteDOMSetAttribute', { node, attribute, value });
}
function set_data_dev(text, data) {
    data = '' + data;
    if (text.wholeText === data)
        return;
    dispatch_dev('SvelteDOMSetData', { node: text, data });
    text.data = data;
}
function validate_slots(name, slot, keys) {
    for (const slot_key of Object.keys(slot)) {
        if (!~keys.indexOf(slot_key)) {
            console.warn(`<${name}> received an unexpected slot "${slot_key}".`);
        }
    }
}
/**
 * Base class for Svelte components with some minor dev-enhancements. Used when dev=true.
 */
class SvelteComponentDev extends SvelteComponent {
    constructor(options) {
        if (!options || (!options.target && !options.$$inline)) {
            throw new Error("'target' is a required option");
        }
        super();
    }
    $destroy() {
        super.$destroy();
        this.$destroy = () => {
            console.warn('Component was already destroyed'); // eslint-disable-line no-console
        };
    }
    $capture_state() { }
    $inject_state() { }
}

/**
 * The UserAgent for IPad has changed since IOS 13.
 * Now it looks the same as for MacOS, so there is no `IPad` keyword anymore.
 * So we need to check **maxTouchPoints** to know if it is an IPad
 * */
function isAppleHandheld() {
    return (/iP(ad|od|hone)/i.test(navigator.userAgent) ||
        (navigator.userAgent.toLowerCase().indexOf('macintosh') > -1 &&
            navigator.maxTouchPoints &&
            navigator.maxTouchPoints > 2));
}
function isChromeiOS() {
    return /CriOS/.test(navigator.userAgent);
}
function isChromeMobile() {
    return /Chrome\/[.0-9]* Mobile/i.test(navigator.userAgent);
}
function isFirefox() {
    return /Firefox/i.test(navigator.userAgent);
}
function isFirefoxiOS() {
    return /FxiOS/.test(navigator.userAgent);
}
function isOpera() {
    return /OPR/.test(navigator.userAgent) || /Opera/.test(navigator.userAgent);
}
function isEdge() {
    return /Edg/.test(navigator.userAgent);
}
function isBrave() {
    return navigator['brave'] && typeof navigator['brave'].isBrave === 'function';
}
function isAndroid() {
    return /Android/.test(navigator.userAgent);
}
// @ts-ignore
const isNativeApp = () => !!window.ReactNativeWebView;
function isAndroidWebView() {
    return /wv/.test(navigator.userAgent) || (/Android/.test(navigator.userAgent) && isNativeApp());
}
function isMobile() {
    return (typeof window.orientation !== 'undefined' || navigator.userAgent.indexOf('IEMobile') !== -1);
}
/** Generate a return url that Proton will redirect back to w/o reload. */
function generateReturnUrl() {
    if (isChromeiOS()) {
        // google chrome on iOS will always open new tab so we just ask it to open again as a workaround
        return 'googlechrome://';
    }
    if (isFirefoxiOS()) {
        // same for firefox
        return 'firefox:://';
    }
    if (isAppleHandheld() && isBrave()) {
        // and brave ios
        return 'brave://';
    }
    if (isAppleHandheld()) {
        // return url with unique fragment required for iOS safari to trigger the return url
        const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let rv = window.location.href.split('#')[0] + '#';
        for (let i = 0; i < 8; i++) {
            rv += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
        }
        return rv;
    }
    if (isAndroid() && isFirefox()) {
        return 'android-app://org.mozilla.firefox';
    }
    if (isAndroid() && isEdge()) {
        return 'android-app://com.microsoft.emmx';
    }
    if (isAndroid() && isOpera()) {
        return 'android-app://com.opera.browser';
    }
    if (isAndroid() && isBrave()) {
        return 'android-app://com.brave.browser';
    }
    if (isAndroid() && isAndroidWebView()) {
        return 'android-app://webview';
    }
    if (isAndroid() && isChromeMobile()) {
        return 'android-app://com.android.chrome';
    }
    return window.location.href;
}
function parseErrorMessage(error) {
    let errorMessage;
    if (error.json && error.json.error) {
        error = error.json.error;
    }
    if (error.error) {
        error = error.error;
    }
    if (error.details) {
        const { code, details, name, what } = error;
        if (name === 'eosio_assert_message_exception') {
            errorMessage = details[0].message.replace('assertion failure with message: ', '');
        }
        else if (details.length > 0) {
            errorMessage = details.map((d) => d.message).join('\n');
        }
        else {
            errorMessage = what || String(error);
        }
    }
    else {
        errorMessage = error.message || String(error);
    }
    return errorMessage;
}
const footnoteLinks = {
    proton: 'https://xprnetwork.org/wallet',
    anchor: 'https://greymass.com/en/anchor/',
};
function getFootnoteLink(walletType) {
    return footnoteLinks[walletType] || '';
}

/* src/views/CountDown.svelte generated by Svelte v3.49.0 */

const file$2 = "src/views/CountDown.svelte";

function add_css$2(target) {
	append_styles(target, "svelte-8ofljp", ".proton-link-countdown{width:104px;height:104px;display:flex;align-items:center;justify-content:center;margin:40px auto 5px;border-radius:100%;background-color:white;font-size:28px;line-height:40px}\n/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiQ291bnREb3duLnN2ZWx0ZSIsIm1hcHBpbmdzIjoiQUFDSSxzQkFBQSxBQUFBLENBQUEsQUFDSSxLQUFBLENBQUEsS0FBQSxDQUNBLE1BQUEsQ0FBQSxLQUFBLENBQ0EsT0FBQSxDQUFBLElBQUEsQ0FDQSxXQUFBLENBQUEsTUFBQSxDQUNBLGVBQUEsQ0FBQSxNQUFBLENBQ0EsTUFBQSxDQUFBLElBQUEsQ0FBQSxJQUFBLENBQUEsR0FBQSxDQUNBLGFBQUEsQ0FBQSxJQUFBLENBQ0EsZ0JBQUEsQ0FBQSxLQUFBLENBQ0EsU0FBQSxDQUFBLElBQUEsQ0FDQSxXQUFBLENBQUEsSUFBQSxBQ01SLENBRUEiLCJuYW1lcyI6W10sInNvdXJjZXMiOlsiZmlsZTovLy9Vc2Vycy9qYWZyaS9Xb3JrL3Byb3Rvbi9wcm90b24td2ViLXNkay9wYWNrYWdlcy9wcm90b24tYnJvd3Nlci10cmFuc3BvcnQvc3JjL3ZpZXdzL0NvdW50RG93bi5zdmVsdGUiLCJDb3VudERvd24uc3ZlbHRlIl19 */");
}

function create_fragment$2(ctx) {
	let div;
	let t;

	const block = {
		c: function create() {
			div = element("div");
			t = text(/*countDown*/ ctx[0]);
			attr_dev(div, "class", "proton-link-countdown");
			add_location(div, file$2, 3, 0, 61);
		},
		l: function claim(nodes) {
			throw new Error("options.hydrate only works if the component was compiled with the `hydratable: true` option");
		},
		m: function mount(target, anchor) {
			insert_dev(target, div, anchor);
			append_dev(div, t);
		},
		p: function update(ctx, [dirty]) {
			if (dirty & /*countDown*/ 1) set_data_dev(t, /*countDown*/ ctx[0]);
		},
		i: noop,
		o: noop,
		d: function destroy(detaching) {
			if (detaching) detach_dev(div);
		}
	};

	dispatch_dev("SvelteRegisterBlock", {
		block,
		id: create_fragment$2.name,
		type: "component",
		source: "",
		ctx
	});

	return block;
}

function instance$2($$self, $$props, $$invalidate) {
	let { $$slots: slots = {}, $$scope } = $$props;
	validate_slots('CountDown', slots, []);
	let { countDown = '00:00' } = $$props;
	const writable_props = ['countDown'];

	Object.keys($$props).forEach(key => {
		if (!~writable_props.indexOf(key) && key.slice(0, 2) !== '$$' && key !== 'slot') console.warn(`<CountDown> was created with unknown prop '${key}'`);
	});

	$$self.$$set = $$props => {
		if ('countDown' in $$props) $$invalidate(0, countDown = $$props.countDown);
	};

	$$self.$capture_state = () => ({ countDown });

	$$self.$inject_state = $$props => {
		if ('countDown' in $$props) $$invalidate(0, countDown = $$props.countDown);
	};

	if ($$props && "$$inject" in $$props) {
		$$self.$inject_state($$props.$$inject);
	}

	return [countDown];
}

class CountDown extends SvelteComponentDev {
	constructor(options) {
		super(options);
		init(this, options, instance$2, create_fragment$2, safe_not_equal, { countDown: 0 }, add_css$2);

		dispatch_dev("SvelteRegisterComponent", {
			component: this,
			tagName: "CountDown",
			options,
			id: create_fragment$2.name
		});
	}

	get countDown() {
		throw new Error("<CountDown>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set countDown(value) {
		throw new Error("<CountDown>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}
}

/* src/views/QRCode.svelte generated by Svelte v3.49.0 */
const file$1 = "src/views/QRCode.svelte";

function add_css$1(target) {
	append_styles(target, "svelte-1xxtybb", ".proton-link-actions{display:flex;flex-direction:column;align-items:center;position:relative}.proton-link-background{width:250px;height:250px;border-radius:10px;box-shadow:0 4px 8px -2px rgba(141, 141, 148, 0.28), 0 0 2px 0 rgba(141, 141, 148, 0.16);background-color:#ffffff;position:relative;z-index:10}.proton-link-qr{width:100%;height:100%;padding:10px}.proton-link-separator{margin-top:20px;width:100%;font-size:12px;display:flex;align-items:center;text-align:center;color:white}.proton-link-separator::before,.proton-link-separator::after{content:\"\";flex:1;opacity:0.2;border-bottom:1px solid #d8d8d8}.proton-link-separator::before{margin-right:0.5em}.proton-link-separator::after{margin-left:0.5em}.proton-link-uri{width:100%;padding:20px 0px 0px 0px}.proton-link-uri a{width:100%;background-color:rgba(255, 255, 255, 0.3);font-family:\"Circular Std Book\", sans-serif;font-size:16px;font-weight:500;text-align:center;color:#ffffff;text-decoration:none;flex-grow:1;flex:1;padding:18px 0px 16px 0px;display:block;border-radius:10px}.proton-link-uri a:hover{background-color:rgba(255, 255, 255, 0.25);transition:0.2s ease}.proton-link-wskeepalive{display:none}\n/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");
}

function create_fragment$1(ctx) {
	let div3;
	let div0;
	let img;
	let img_src_value;
	let t0;
	let div1;
	let t2;
	let div2;
	let a;
	let t3;
	let t4;
	let iframe;
	let iframe_src_value;
	let mounted;
	let dispose;

	const block = {
		c: function create() {
			div3 = element("div");
			div0 = element("div");
			img = element("img");
			t0 = space();
			div1 = element("div");
			div1.textContent = "OR";
			t2 = space();
			div2 = element("div");
			a = element("a");
			t3 = text("Open Wallet");
			t4 = space();
			iframe = element("iframe");
			attr_dev(img, "class", "proton-link-qr");
			if (!src_url_equal(img.src, img_src_value = /*code*/ ctx[0])) attr_dev(img, "src", img_src_value);
			attr_dev(img, "alt", "auth link");
			add_location(img, file$1, 18, 8, 433);
			attr_dev(div0, "class", "proton-link-background");
			add_location(div0, file$1, 17, 4, 388);
			attr_dev(div1, "class", "proton-link-separator");
			add_location(div1, file$1, 20, 4, 506);
			attr_dev(a, "class", "proton-link-button");
			attr_dev(a, "href", /*link*/ ctx[1]);
			add_location(a, file$1, 22, 8, 592);
			attr_dev(div2, "class", "proton-link-uri");
			add_location(div2, file$1, 21, 4, 554);
			attr_dev(iframe, "class", "proton-link-wskeepalive");
			if (!src_url_equal(iframe.src, iframe_src_value = /*iframeUrl*/ ctx[2])) attr_dev(iframe, "src", iframe_src_value);
			attr_dev(iframe, "title", "keepalive");
			add_location(iframe, file$1, 26, 4, 729);
			attr_dev(div3, "class", "proton-link-actions");
			add_location(div3, file$1, 16, 0, 350);
		},
		l: function claim(nodes) {
			throw new Error("options.hydrate only works if the component was compiled with the `hydratable: true` option");
		},
		m: function mount(target, anchor) {
			insert_dev(target, div3, anchor);
			append_dev(div3, div0);
			append_dev(div0, img);
			append_dev(div3, t0);
			append_dev(div3, div1);
			append_dev(div3, t2);
			append_dev(div3, div2);
			append_dev(div2, a);
			append_dev(a, t3);
			append_dev(div3, t4);
			append_dev(div3, iframe);

			if (!mounted) {
				dispose = listen_dev(a, "click", prevent_default(/*protonLinkClick*/ ctx[3]), false, true, false);
				mounted = true;
			}
		},
		p: function update(ctx, [dirty]) {
			if (dirty & /*code*/ 1 && !src_url_equal(img.src, img_src_value = /*code*/ ctx[0])) {
				attr_dev(img, "src", img_src_value);
			}

			if (dirty & /*link*/ 2) {
				attr_dev(a, "href", /*link*/ ctx[1]);
			}

			if (dirty & /*iframeUrl*/ 4 && !src_url_equal(iframe.src, iframe_src_value = /*iframeUrl*/ ctx[2])) {
				attr_dev(iframe, "src", iframe_src_value);
			}
		},
		i: noop,
		o: noop,
		d: function destroy(detaching) {
			if (detaching) detach_dev(div3);
			mounted = false;
			dispose();
		}
	};

	dispatch_dev("SvelteRegisterBlock", {
		block,
		id: create_fragment$1.name,
		type: "component",
		source: "",
		ctx
	});

	return block;
}

function instance$1($$self, $$props, $$invalidate) {
	let { $$slots: slots = {}, $$scope } = $$props;
	validate_slots('QRCode', slots, []);
	let { code = '' } = $$props;
	let { link = '' } = $$props;
	let iframeUrl = 'about:blank';

	function protonLinkClick() {
		if (link) {
			if (isFirefox() || isBrave()) {
				$$invalidate(2, iframeUrl = link);
			} else {
				window.location.href = link;
			}
		}
	}

	const writable_props = ['code', 'link'];

	Object.keys($$props).forEach(key => {
		if (!~writable_props.indexOf(key) && key.slice(0, 2) !== '$$' && key !== 'slot') console.warn(`<QRCode> was created with unknown prop '${key}'`);
	});

	$$self.$$set = $$props => {
		if ('code' in $$props) $$invalidate(0, code = $$props.code);
		if ('link' in $$props) $$invalidate(1, link = $$props.link);
	};

	$$self.$capture_state = () => ({
		isBrave,
		isFirefox,
		code,
		link,
		iframeUrl,
		protonLinkClick
	});

	$$self.$inject_state = $$props => {
		if ('code' in $$props) $$invalidate(0, code = $$props.code);
		if ('link' in $$props) $$invalidate(1, link = $$props.link);
		if ('iframeUrl' in $$props) $$invalidate(2, iframeUrl = $$props.iframeUrl);
	};

	if ($$props && "$$inject" in $$props) {
		$$self.$inject_state($$props.$$inject);
	}

	return [code, link, iframeUrl, protonLinkClick];
}

class QRCode$1 extends SvelteComponentDev {
	constructor(options) {
		super(options);
		init(this, options, instance$1, create_fragment$1, safe_not_equal, { code: 0, link: 1 }, add_css$1);

		dispatch_dev("SvelteRegisterComponent", {
			component: this,
			tagName: "QRCode",
			options,
			id: create_fragment$1.name
		});
	}

	get code() {
		throw new Error("<QRCode>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set code(value) {
		throw new Error("<QRCode>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	get link() {
		throw new Error("<QRCode>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set link(value) {
		throw new Error("<QRCode>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}
}

/* src/views/Dialog.svelte generated by Svelte v3.49.0 */
const file = "src/views/Dialog.svelte";

function add_css(target) {
	append_styles(target, "svelte-1qqw1l8", ".proton-link{font-family:\"Circular Std Book\", -apple-system, system-ui, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;font-size:13px;background:rgba(0, 0, 0, 0.65);position:fixed;top:0px;left:0px;width:100%;height:100%;z-index:2147483647;display:none;align-items:center;justify-content:center}.proton-link *{box-sizing:border-box;line-height:1}.proton-link-button{color:white;cursor:pointer;text-align:center;background-color:transparent;background-image:none;outline:none}.proton-link-subtitle{font-size:16px;line-height:24px;display:block;margin-top:10px;text-align:center;word-break:break-word}.proton-link--anchor .proton-link-inner{background:#3650a2;color:white}.proton-link--anchor .proton-link-logo{background-image:url(\"data:image/png;base64,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\")}.proton-link--anchor .proton-link-countdown{color:#3650a2}.proton-link--proton .proton-link-inner{background:#752eeb;color:white}.proton-link--proton .proton-link-logo{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='49' viewBox='0 0 160 49'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFF'%3E%3Cg%3E%3Cpath fill-rule='nonzero' d='M58.895 32.064V27.53h3.547c1.361 0 2.547-.224 3.557-.673 1.01-.449 1.785-1.096 2.323-1.942.539-.845.808-1.836.808-2.974 0-1.152-.269-2.147-.808-2.985-.538-.838-1.313-1.481-2.323-1.93-1.01-.449-2.196-.673-3.557-.673h-6.464v15.711h2.917zm3.412-7.003h-3.412v-6.24h3.412c1.272 0 2.237.27 2.895.809.659.538.988 1.309.988 2.311 0 1.003-.33 1.774-.988 2.312-.658.539-1.623.808-2.895.808zm13.942 7.003v-4.579h3.633c.226-.001.4-.009.519-.022l3.21 4.601h3.142l-3.614-5.162c1.077-.42 1.904-1.055 2.48-1.908.576-.853.864-1.87.864-3.053 0-1.152-.269-2.147-.808-2.985-.538-.838-1.313-1.481-2.323-1.93-1.01-.449-2.195-.673-3.557-.673H73.33v15.711h2.918zm3.411-6.98H76.25v-6.262h3.411c1.272 0 2.237.269 2.896.808.658.538.987 1.309.987 2.311 0 1.003-.329 1.777-.987 2.324-.659.546-1.624.819-2.896.819zm18.7 7.205c1.601 0 3.045-.348 4.332-1.044 1.287-.696 2.297-1.657 3.03-2.884.733-1.227 1.1-2.611 1.1-4.153 0-1.54-.367-2.925-1.1-4.152-.733-1.227-1.743-2.188-3.03-2.884-1.287-.696-2.73-1.044-4.332-1.044-1.601 0-3.045.348-4.332 1.044-1.287.696-2.297 1.66-3.03 2.895-.733 1.235-1.1 2.615-1.1 4.141 0 1.527.367 2.907 1.1 4.141.733 1.235 1.743 2.2 3.03 2.896 1.287.696 2.73 1.044 4.332 1.044zm0-2.56c-1.047 0-1.99-.235-2.828-.706-.838-.471-1.496-1.13-1.975-1.975-.479-.846-.718-1.792-.718-2.84 0-1.047.239-1.993.718-2.839.479-.845 1.137-1.504 1.975-1.975.838-.471 1.78-.707 2.828-.707 1.047 0 1.99.236 2.828.707.838.471 1.496 1.13 1.975 1.975.479.846.718 1.792.718 2.84 0 1.047-.239 1.993-.718 2.839-.479.845-1.137 1.504-1.975 1.975-.838.471-1.78.707-2.828.707zm18.722 2.335V18.822h5.207v-2.47h-13.332v2.47h5.207v13.242h2.918zm15.805.225c1.6 0 3.045-.348 4.332-1.044 1.286-.696 2.296-1.657 3.03-2.884.733-1.227 1.1-2.611 1.1-4.153 0-1.54-.367-2.925-1.1-4.152-.734-1.227-1.744-2.188-3.03-2.884-1.287-.696-2.731-1.044-4.332-1.044s-3.045.348-4.332 1.044c-1.287.696-2.297 1.66-3.03 2.895-.733 1.235-1.1 2.615-1.1 4.141 0 1.527.367 2.907 1.1 4.141.733 1.235 1.743 2.2 3.03 2.896 1.287.696 2.73 1.044 4.332 1.044zm0-2.56c-1.048 0-1.99-.235-2.828-.706-.838-.471-1.497-1.13-1.976-1.975-.478-.846-.718-1.792-.718-2.84 0-1.047.24-1.993.718-2.839.48-.845 1.138-1.504 1.976-1.975.838-.471 1.78-.707 2.828-.707 1.047 0 1.99.236 2.828.707.838.471 1.496 1.13 1.975 1.975.479.846.718 1.792.718 2.84 0 1.047-.24 1.993-.718 2.839-.479.845-1.137 1.504-1.975 1.975-.838.471-1.78.707-2.828.707zm15.737 2.335V21.425l8.663 10.64h2.402V16.352h-2.895v10.639l-8.664-10.64h-2.402v15.712h2.896z' transform='translate(-432 -207) translate(432 207)'/%3E%3Cg%3E%3Cpath d='M21.582 0c3.104 0 5.782 3.222 7.545 8.459-.712.198-1.44.42-2.174.666-1.465-4.331-3.476-6.88-5.37-6.88-2.136 0-4.415 3.232-5.899 8.606.58.187 1.165.386 1.762.606 1.346.495 2.735 1.078 4.15 1.74.081-.038.162-.078.243-.115.907-.417 1.808-.801 2.7-1.154l.32-.125c2.671-1.033 5.252-1.773 7.616-2.165 5.088-.846 8.666.03 10.07 2.466 1.409 2.437.38 5.974-2.898 9.956-.189.228-.38.459-.583.686-.488-.542-1.007-1.083-1.561-1.625 2.891-3.354 4.03-6.285 3.1-7.895-.879-1.523-3.777-2.036-7.76-1.375-.85.142-1.735.333-2.643.57.13.596.25 1.205.357 1.835.246 1.41.436 2.903.57 4.458.7.492 1.384.991 2.04 1.5 2.572 1.99 4.78 4.078 6.48 6.143 3.277 3.982 4.307 7.519 2.899 9.956-1.055 1.83-3.338 2.78-6.567 2.78-1.067 0-2.238-.103-3.504-.315-.328-.054-.661-.115-.997-.182.201-.696.388-1.424.557-2.178.273.055.542.103.808.148 3.98.66 6.881.147 7.76-1.376 1.068-1.85-.6-5.449-4.522-9.428-.391.357-.794.713-1.21 1.066-.062.051-.126.106-.187.154-1.11.93-2.302 1.842-3.561 2.728-.06.704-.13 1.4-.213 2.075-1.225 9.863-4.829 16.632-9.328 16.632-3.097 0-5.771-3.213-7.535-8.436.709-.193 1.434-.417 2.175-.667 1.465 4.319 3.469 6.858 5.36 6.858 2.136 0 4.42-3.243 5.907-8.631-.583-.186-1.17-.383-1.764-.603-1.355-.494-2.738-1.07-4.13-1.716l-.09.042c-.785.366-1.558.706-2.321 1.02-.119.048-.234.096-.35.138-4.392 1.77-8.404 2.687-11.594 2.687-3.177 0-5.534-.911-6.624-2.796-1.559-2.7-.084-6.654 3.607-10.822l1.568 1.654c-2.985 3.419-4.172 6.413-3.23 8.045 1.067 1.849 5.006 2.206 10.402.805-.127-.595-.248-1.202-.355-1.83-.244-1.41-.433-2.898-.569-4.45-.703-.491-1.387-.988-2.035-1.486C3.752 23.42 0 18.312 0 14.51c0-.879.202-1.687.616-2.405C2.168 9.42 6.288 8.712 11.697 9.796c-.205.705-.394 1.442-.564 2.206-4.448-.879-7.632-.408-8.571 1.224-1.067 1.848.594 5.44 4.504 9.413.502-.455 1.022-.907 1.569-1.355 1.053-.871 2.194-1.736 3.407-2.586.061-.706.13-1.403.216-2.08C13.483 6.767 17.087 0 21.582 0zm7.103 31.336c-.54.342-1.087.68-1.645 1.01-.19.113-.379.222-.568.33-.664.382-1.327.75-1.985 1.097-.097.052-.194.1-.29.152.681.288 1.358.558 2.028.807.605.225 1.202.431 1.794.625.132-.615.254-1.248.364-1.904.115-.681.212-1.393.302-2.117zm-14.212-.003c.094.76.203 1.5.325 2.212.105.624.224 1.224.35 1.81.363-.118.73-.24 1.102-.373.112-.039.228-.08.34-.122.766-.276 1.552-.58 2.353-.923l.023-.01c-.759-.397-1.518-.814-2.273-1.25-.039-.023-.08-.045-.12-.071-.72-.418-1.418-.844-2.1-1.273zm7.11-15.65c-.767.373-1.537.765-2.306 1.183-.487.263-.975.535-1.462.817-1.277.738-2.48 1.495-3.615 2.261-.046.654-.086 1.316-.111 1.997-.029.741-.042 1.498-.042 2.267 0 1.476.054 2.897.151 4.263.655.443 1.329.882 2.033 1.313.516.321 1.042.638 1.584.95 1.254.724 2.515 1.39 3.77 2 .08-.04.16-.075.241-.115.834-.41 1.677-.853 2.533-1.328.33-.18.66-.365.99-.558 1.278-.736 2.481-1.493 3.615-2.26.047-.654.087-1.317.112-1.998.029-.74.041-1.497.041-2.267 0-1.475-.054-2.895-.15-4.262-.653-.44-1.326-.88-2.03-1.314-.516-.32-1.045-.634-1.587-.949-1.277-.737-2.535-1.4-3.767-2zm0 5.64c1.593 0 2.885 1.292 2.885 2.885 0 1.594-1.292 2.886-2.886 2.886-1.593 0-2.885-1.292-2.885-2.886 0-1.593 1.292-2.885 2.885-2.885zm9.729.3c.033.847.05 1.71.05 2.585 0 .212 0 .424-.003.635-.007.663-.027 1.315-.052 1.96.578-.436 1.132-.874 1.661-1.312.068-.055.132-.106.196-.16.449-.376.878-.75 1.29-1.123-.378-.34-.77-.683-1.178-1.026-.619-.522-1.278-1.042-1.964-1.56zm-19.454-.006c-.52.392-1.021.785-1.501 1.177-.579.47-1.127.943-1.649 1.416.379.341.771.684 1.18 1.028.62.52 1.279 1.039 1.966 1.557-.034-.847-.051-1.71-.051-2.587 0-.211 0-.423.006-.631.005-.662.024-1.314.049-1.96zm16.158-8.556c-.3.097-.6.198-.903.303-.891.308-1.795.658-2.712 1.042l-.195.084c.75.396 1.507.812 2.266 1.25.039.023.08.045.119.07.717.414 1.414.842 2.1 1.277-.093-.762-.202-1.502-.324-2.216-.107-.623-.226-1.224-.351-1.81zm-12.87 0c-.13.613-.251 1.246-.361 1.9-.117.682-.215 1.391-.305 2.115.684-.432 1.387-.859 2.11-1.278l.103-.058c.757-.437 1.517-.853 2.276-1.25-.688-.29-1.364-.56-2.026-.805-.613-.23-1.21-.435-1.796-.624z' transform='translate(-432 -207) translate(432 207)'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.proton-link--proton .proton-link-countdown{color:#752eeb}.proton-link--webauth .proton-link-inner{background:#752eeb;color:white}.proton-link--webauth .proton-link-logo{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='49' viewBox='0 0 160 49'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFF'%3E%3Cg%3E%3Cpath fill-rule='nonzero' d='M58.895 32.064V27.53h3.547c1.361 0 2.547-.224 3.557-.673 1.01-.449 1.785-1.096 2.323-1.942.539-.845.808-1.836.808-2.974 0-1.152-.269-2.147-.808-2.985-.538-.838-1.313-1.481-2.323-1.93-1.01-.449-2.196-.673-3.557-.673h-6.464v15.711h2.917zm3.412-7.003h-3.412v-6.24h3.412c1.272 0 2.237.27 2.895.809.659.538.988 1.309.988 2.311 0 1.003-.33 1.774-.988 2.312-.658.539-1.623.808-2.895.808zm13.942 7.003v-4.579h3.633c.226-.001.4-.009.519-.022l3.21 4.601h3.142l-3.614-5.162c1.077-.42 1.904-1.055 2.48-1.908.576-.853.864-1.87.864-3.053 0-1.152-.269-2.147-.808-2.985-.538-.838-1.313-1.481-2.323-1.93-1.01-.449-2.195-.673-3.557-.673H73.33v15.711h2.918zm3.411-6.98H76.25v-6.262h3.411c1.272 0 2.237.269 2.896.808.658.538.987 1.309.987 2.311 0 1.003-.329 1.777-.987 2.324-.659.546-1.624.819-2.896.819zm18.7 7.205c1.601 0 3.045-.348 4.332-1.044 1.287-.696 2.297-1.657 3.03-2.884.733-1.227 1.1-2.611 1.1-4.153 0-1.54-.367-2.925-1.1-4.152-.733-1.227-1.743-2.188-3.03-2.884-1.287-.696-2.73-1.044-4.332-1.044-1.601 0-3.045.348-4.332 1.044-1.287.696-2.297 1.66-3.03 2.895-.733 1.235-1.1 2.615-1.1 4.141 0 1.527.367 2.907 1.1 4.141.733 1.235 1.743 2.2 3.03 2.896 1.287.696 2.73 1.044 4.332 1.044zm0-2.56c-1.047 0-1.99-.235-2.828-.706-.838-.471-1.496-1.13-1.975-1.975-.479-.846-.718-1.792-.718-2.84 0-1.047.239-1.993.718-2.839.479-.845 1.137-1.504 1.975-1.975.838-.471 1.78-.707 2.828-.707 1.047 0 1.99.236 2.828.707.838.471 1.496 1.13 1.975 1.975.479.846.718 1.792.718 2.84 0 1.047-.239 1.993-.718 2.839-.479.845-1.137 1.504-1.975 1.975-.838.471-1.78.707-2.828.707zm18.722 2.335V18.822h5.207v-2.47h-13.332v2.47h5.207v13.242h2.918zm15.805.225c1.6 0 3.045-.348 4.332-1.044 1.286-.696 2.296-1.657 3.03-2.884.733-1.227 1.1-2.611 1.1-4.153 0-1.54-.367-2.925-1.1-4.152-.734-1.227-1.744-2.188-3.03-2.884-1.287-.696-2.731-1.044-4.332-1.044s-3.045.348-4.332 1.044c-1.287.696-2.297 1.66-3.03 2.895-.733 1.235-1.1 2.615-1.1 4.141 0 1.527.367 2.907 1.1 4.141.733 1.235 1.743 2.2 3.03 2.896 1.287.696 2.73 1.044 4.332 1.044zm0-2.56c-1.048 0-1.99-.235-2.828-.706-.838-.471-1.497-1.13-1.976-1.975-.478-.846-.718-1.792-.718-2.84 0-1.047.24-1.993.718-2.839.48-.845 1.138-1.504 1.976-1.975.838-.471 1.78-.707 2.828-.707 1.047 0 1.99.236 2.828.707.838.471 1.496 1.13 1.975 1.975.479.846.718 1.792.718 2.84 0 1.047-.24 1.993-.718 2.839-.479.845-1.137 1.504-1.975 1.975-.838.471-1.78.707-2.828.707zm15.737 2.335V21.425l8.663 10.64h2.402V16.352h-2.895v10.639l-8.664-10.64h-2.402v15.712h2.896z' transform='translate(-432 -207) translate(432 207)'/%3E%3Cg%3E%3Cpath d='M21.582 0c3.104 0 5.782 3.222 7.545 8.459-.712.198-1.44.42-2.174.666-1.465-4.331-3.476-6.88-5.37-6.88-2.136 0-4.415 3.232-5.899 8.606.58.187 1.165.386 1.762.606 1.346.495 2.735 1.078 4.15 1.74.081-.038.162-.078.243-.115.907-.417 1.808-.801 2.7-1.154l.32-.125c2.671-1.033 5.252-1.773 7.616-2.165 5.088-.846 8.666.03 10.07 2.466 1.409 2.437.38 5.974-2.898 9.956-.189.228-.38.459-.583.686-.488-.542-1.007-1.083-1.561-1.625 2.891-3.354 4.03-6.285 3.1-7.895-.879-1.523-3.777-2.036-7.76-1.375-.85.142-1.735.333-2.643.57.13.596.25 1.205.357 1.835.246 1.41.436 2.903.57 4.458.7.492 1.384.991 2.04 1.5 2.572 1.99 4.78 4.078 6.48 6.143 3.277 3.982 4.307 7.519 2.899 9.956-1.055 1.83-3.338 2.78-6.567 2.78-1.067 0-2.238-.103-3.504-.315-.328-.054-.661-.115-.997-.182.201-.696.388-1.424.557-2.178.273.055.542.103.808.148 3.98.66 6.881.147 7.76-1.376 1.068-1.85-.6-5.449-4.522-9.428-.391.357-.794.713-1.21 1.066-.062.051-.126.106-.187.154-1.11.93-2.302 1.842-3.561 2.728-.06.704-.13 1.4-.213 2.075-1.225 9.863-4.829 16.632-9.328 16.632-3.097 0-5.771-3.213-7.535-8.436.709-.193 1.434-.417 2.175-.667 1.465 4.319 3.469 6.858 5.36 6.858 2.136 0 4.42-3.243 5.907-8.631-.583-.186-1.17-.383-1.764-.603-1.355-.494-2.738-1.07-4.13-1.716l-.09.042c-.785.366-1.558.706-2.321 1.02-.119.048-.234.096-.35.138-4.392 1.77-8.404 2.687-11.594 2.687-3.177 0-5.534-.911-6.624-2.796-1.559-2.7-.084-6.654 3.607-10.822l1.568 1.654c-2.985 3.419-4.172 6.413-3.23 8.045 1.067 1.849 5.006 2.206 10.402.805-.127-.595-.248-1.202-.355-1.83-.244-1.41-.433-2.898-.569-4.45-.703-.491-1.387-.988-2.035-1.486C3.752 23.42 0 18.312 0 14.51c0-.879.202-1.687.616-2.405C2.168 9.42 6.288 8.712 11.697 9.796c-.205.705-.394 1.442-.564 2.206-4.448-.879-7.632-.408-8.571 1.224-1.067 1.848.594 5.44 4.504 9.413.502-.455 1.022-.907 1.569-1.355 1.053-.871 2.194-1.736 3.407-2.586.061-.706.13-1.403.216-2.08C13.483 6.767 17.087 0 21.582 0zm7.103 31.336c-.54.342-1.087.68-1.645 1.01-.19.113-.379.222-.568.33-.664.382-1.327.75-1.985 1.097-.097.052-.194.1-.29.152.681.288 1.358.558 2.028.807.605.225 1.202.431 1.794.625.132-.615.254-1.248.364-1.904.115-.681.212-1.393.302-2.117zm-14.212-.003c.094.76.203 1.5.325 2.212.105.624.224 1.224.35 1.81.363-.118.73-.24 1.102-.373.112-.039.228-.08.34-.122.766-.276 1.552-.58 2.353-.923l.023-.01c-.759-.397-1.518-.814-2.273-1.25-.039-.023-.08-.045-.12-.071-.72-.418-1.418-.844-2.1-1.273zm7.11-15.65c-.767.373-1.537.765-2.306 1.183-.487.263-.975.535-1.462.817-1.277.738-2.48 1.495-3.615 2.261-.046.654-.086 1.316-.111 1.997-.029.741-.042 1.498-.042 2.267 0 1.476.054 2.897.151 4.263.655.443 1.329.882 2.033 1.313.516.321 1.042.638 1.584.95 1.254.724 2.515 1.39 3.77 2 .08-.04.16-.075.241-.115.834-.41 1.677-.853 2.533-1.328.33-.18.66-.365.99-.558 1.278-.736 2.481-1.493 3.615-2.26.047-.654.087-1.317.112-1.998.029-.74.041-1.497.041-2.267 0-1.475-.054-2.895-.15-4.262-.653-.44-1.326-.88-2.03-1.314-.516-.32-1.045-.634-1.587-.949-1.277-.737-2.535-1.4-3.767-2zm0 5.64c1.593 0 2.885 1.292 2.885 2.885 0 1.594-1.292 2.886-2.886 2.886-1.593 0-2.885-1.292-2.885-2.886 0-1.593 1.292-2.885 2.885-2.885zm9.729.3c.033.847.05 1.71.05 2.585 0 .212 0 .424-.003.635-.007.663-.027 1.315-.052 1.96.578-.436 1.132-.874 1.661-1.312.068-.055.132-.106.196-.16.449-.376.878-.75 1.29-1.123-.378-.34-.77-.683-1.178-1.026-.619-.522-1.278-1.042-1.964-1.56zm-19.454-.006c-.52.392-1.021.785-1.501 1.177-.579.47-1.127.943-1.649 1.416.379.341.771.684 1.18 1.028.62.52 1.279 1.039 1.966 1.557-.034-.847-.051-1.71-.051-2.587 0-.211 0-.423.006-.631.005-.662.024-1.314.049-1.96zm16.158-8.556c-.3.097-.6.198-.903.303-.891.308-1.795.658-2.712 1.042l-.195.084c.75.396 1.507.812 2.266 1.25.039.023.08.045.119.07.717.414 1.414.842 2.1 1.277-.093-.762-.202-1.502-.324-2.216-.107-.623-.226-1.224-.351-1.81zm-12.87 0c-.13.613-.251 1.246-.361 1.9-.117.682-.215 1.391-.305 2.115.684-.432 1.387-.859 2.11-1.278l.103-.058c.757-.437 1.517-.853 2.276-1.25-.688-.29-1.364-.56-2.026-.805-.613-.23-1.21-.435-1.796-.624z' transform='translate(-432 -207) translate(432 207)'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.proton-link--webauth .proton-link-countdown{color:#752eeb}.proton-link.is-active{display:flex}.proton-link-inner{background-color:white;color:black;margin:20px;position:relative;border-radius:20px;position:relative;box-shadow:0px 4px 100px rgba(0, 0, 0, 0.5);width:360px;transition-property:all;transition-duration:0.5s;transition-timing-function:ease-in-out}.proton-link-nav{height:55px;display:flex;position:relative;border-radius:20px 20px 0px 0px;justify-content:center;align-items:center;padding:0px 16px;background-color:rgba(0, 0, 0, 0.2)}.proton-link-back{width:16px;height:16px;position:absolute;left:16px;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg%3E%3Cg%3E%3Cg%3E%3Cpath d='M0 0L24 0 24 24 0 24z' transform='translate(-348 -152) translate(332 136) translate(16 16)'/%3E%3Cpath fill='rgba(255,255,255, 0.8)' fill-rule='nonzero' d='M16.41 5.791L14.619 4 7 11.619 14.619 19.239 16.41 17.448 10.594 11.619z' transform='translate(-348 -152) translate(332 136) translate(16 16)'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\");background-size:20px;background-repeat:no-repeat;background-position:50%;border-radius:100%;cursor:pointer}.proton-link-back:hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg%3E%3Cg%3E%3Cg%3E%3Cpath d='M0 0L24 0 24 24 0 24z' transform='translate(-348 -152) translate(332 136) translate(16 16)'/%3E%3Cpath fill='rgba(255,255,255, 1)' fill-rule='nonzero' d='M16.41 5.791L14.619 4 7 11.619 14.619 19.239 16.41 17.448 10.594 11.619z' transform='translate(-348 -152) translate(332 136) translate(16 16)'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\")}.proton-link-header{font-family:\"Circular Std Book\", sans-serif;font-size:16px;line-height:24px}.proton-link-close{width:16px;height:16px;position:absolute;right:16px;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.66 10.987L6 7.327l-3.66 3.66A1.035 1.035 0 11.876 9.523l3.66-3.66-3.66-3.66A1.035 1.035 0 012.34.737L6 4.398 9.66.739a1.035 1.035 0 111.464 1.464l-3.66 3.66 3.66 3.661a1.035 1.035 0 11-1.464 1.464z' fill='rgba(255,255,255, 0.8)' fill-rule='nonzero'/%3E%3C/svg%3E\");background-size:14px;background-repeat:no-repeat;background-position:50%;border-radius:100%;cursor:pointer}.proton-link-close:hover{background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.66 10.987L6 7.327l-3.66 3.66A1.035 1.035 0 11.876 9.523l3.66-3.66-3.66-3.66A1.035 1.035 0 012.34.737L6 4.398 9.66.739a1.035 1.035 0 111.464 1.464l-3.66 3.66 3.66 3.661a1.035 1.035 0 11-1.464 1.464z' fill='rgba(255,255,255,1)' fill-rule='nonzero'/%3E%3C/svg%3E\")}.proton-link-logo{width:100%;height:50px;background-size:165px;background-repeat:no-repeat;background-position:50%;margin-bottom:20px}.proton-link-logo.loading{border-radius:100%;background-image:url(\"data:image/svg+xml,%3Csvg viewBox='0.5 0.5 45 45' xmlns='http://www.w3.org/2000/svg' stroke='%23fff'%3E%3Cg fill='none' fill-rule='evenodd' transform='translate(1 1)' stroke-width='2'%3E%3Ccircle cx='22' cy='22' r='6' stroke-opacity='0'%3E%3Canimate attributeName='r' begin='1.5s' dur='3s' values='6;22' calcMode='linear' repeatCount='indefinite' /%3E%3Canimate attributeName='stroke-opacity' begin='1.5s' dur='3s' values='1;0' calcMode='linear' repeatCount='indefinite' /%3E%3Canimate attributeName='stroke-width' begin='1.5s' dur='3s' values='2;0' calcMode='linear' repeatCount='indefinite' /%3E%3C/circle%3E%3Ccircle cx='22' cy='22' r='6' stroke-opacity='0'%3E%3Canimate attributeName='r' begin='3s' dur='3s' values='6;22' calcMode='linear' repeatCount='indefinite' /%3E%3Canimate attributeName='stroke-opacity' begin='3s' dur='3s' values='1;0' calcMode='linear' repeatCount='indefinite' /%3E%3Canimate attributeName='stroke-width' begin='3s' dur='3s' values='2;0' calcMode='linear' repeatCount='indefinite' /%3E%3C/circle%3E%3Ccircle cx='22' cy='22' r='8'%3E%3Canimate attributeName='r' begin='0s' dur='1.5s' values='6;1;2;3;4;5;6' calcMode='linear' repeatCount='indefinite' /%3E%3C/circle%3E%3C/g%3E%3C/svg%3E\");width:104px;height:104px;margin:35px auto 35px;background-size:initial}.proton-link-logo.error{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='38' viewBox='0 0 12 38'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23F94E6C' fill-rule='nonzero'%3E%3Cg%3E%3Cpath d='M11.227 35.73c0 .385-.131.708-.392.97-.26.261-.57.391-.929.391H1.981c-.358 0-.667-.13-.929-.392-.261-.261-.392-.584-.392-.97v-7.842c0-.385.131-.708.392-.97.262-.261.57-.391.929-.391h7.925c.358 0 .668.13.929.392.26.26.392.584.392.969v7.843zM11.145 20.293c-.028.275-.173.501-.434.68-.261.18-.585.268-.97.268H2.104c-.385 0-.715-.09-.99-.268-.276-.18-.413-.405-.413-.681L0 1.428C0 1.043.137.754.412.562.77.259 1.1.108 1.403.108h9.081c.303 0 .633.15.991.454.275.192.412.454.412.784l-.742 18.947z' transform='translate(-506 -305) translate(506 305)'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\");width:104px;height:104px;margin:60px auto 35px;border-radius:100%;background-color:white;background-size:initial}.proton-link-logo.success{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='35' height='26' viewBox='0 0 35 26'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2312C9A0' fill-rule='nonzero'%3E%3Cpath d='M528.59 316.077c0 .548-.22 1.095-.614 1.489l-18.821 18.821c-.394.394-.941.613-1.488.613s-1.095-.219-1.489-.613l-10.899-10.899c-.393-.394-.612-.94-.612-1.488 0-.547.219-1.094.612-1.488l2.977-2.977c.394-.394.941-.612 1.488-.612s1.094.218 1.488.612l6.435 6.457 14.357-14.38c.394-.393.94-.612 1.488-.612.547 0 1.094.219 1.488.613l2.976 2.976c.394.394.613.941.613 1.488z' transform='translate(-494 -311)'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A\");width:104px;height:104px;margin:60px auto 35px;border-radius:100%;background-color:white;background-size:initial}.proton-link-info{text-align:center}.proton-link-title{font-size:28px;line-height:40px;display:block}.proton-link-request{display:flex;flex-direction:column;padding:20px 55px 40px 55px;border-radius:20px;border-top-left-radius:0;border-top-right-radius:0}.proton-link-hr{margin-top:30px;margin-bottom:10px}.proton-link-footnote{font-family:\"Circular Std Book\", sans-serif;font-size:16px;text-align:center;width:100%;position:absolute;bottom:-30px;left:0;color:white}.proton-link-footnote a{color:white;text-decoration:underline}\n/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");
}

// (40:12) {#if showBackButton}
function create_if_block_5(ctx) {
	let div;
	let mounted;
	let dispose;

	const block = {
		c: function create() {
			div = element("div");
			attr_dev(div, "class", "proton-link-back");
			add_location(div, file, 40, 16, 1131);
		},
		m: function mount(target, anchor) {
			insert_dev(target, div, anchor);

			if (!mounted) {
				dispose = listen_dev(div, "click", /*back*/ ctx[9], false, false, false);
				mounted = true;
			}
		},
		p: noop,
		d: function destroy(detaching) {
			if (detaching) detach_dev(div);
			mounted = false;
			dispose();
		}
	};

	dispatch_dev("SvelteRegisterBlock", {
		block,
		id: create_if_block_5.name,
		type: "if",
		source: "(40:12) {#if showBackButton}",
		ctx
	});

	return block;
}

// (47:12) {#if subtitle}
function create_if_block_4(ctx) {
	let span;
	let t;

	const block = {
		c: function create() {
			span = element("span");
			t = text(/*subtitle*/ ctx[3]);
			attr_dev(span, "class", "proton-link-subtitle");
			add_location(span, file, 47, 16, 1421);
		},
		m: function mount(target, anchor) {
			insert_dev(target, span, anchor);
			append_dev(span, t);
		},
		p: function update(ctx, dirty) {
			if (dirty & /*subtitle*/ 8) set_data_dev(t, /*subtitle*/ ctx[3]);
		},
		d: function destroy(detaching) {
			if (detaching) detach_dev(span);
		}
	};

	dispatch_dev("SvelteRegisterBlock", {
		block,
		id: create_if_block_4.name,
		type: "if",
		source: "(47:12) {#if subtitle}",
		ctx
	});

	return block;
}

// (51:12) {#if qrData}
function create_if_block_3(ctx) {
	let qrcode;
	let current;
	const qrcode_spread_levels = [/*qrData*/ ctx[5]];
	let qrcode_props = {};

	for (let i = 0; i < qrcode_spread_levels.length; i += 1) {
		qrcode_props = assign(qrcode_props, qrcode_spread_levels[i]);
	}

	qrcode = new QRCode$1({ props: qrcode_props, $$inline: true });

	const block = {
		c: function create() {
			create_component(qrcode.$$.fragment);
		},
		m: function mount(target, anchor) {
			mount_component(qrcode, target, anchor);
			current = true;
		},
		p: function update(ctx, dirty) {
			const qrcode_changes = (dirty & /*qrData*/ 32)
			? get_spread_update(qrcode_spread_levels, [get_spread_object(/*qrData*/ ctx[5])])
			: {};

			qrcode.$set(qrcode_changes);
		},
		i: function intro(local) {
			if (current) return;
			transition_in(qrcode.$$.fragment, local);
			current = true;
		},
		o: function outro(local) {
			transition_out(qrcode.$$.fragment, local);
			current = false;
		},
		d: function destroy(detaching) {
			destroy_component(qrcode, detaching);
		}
	};

	dispatch_dev("SvelteRegisterBlock", {
		block,
		id: create_if_block_3.name,
		type: "if",
		source: "(51:12) {#if qrData}",
		ctx
	});

	return block;
}

// (55:12) {#if countDown}
function create_if_block_2(ctx) {
	let div;
	let countdown;
	let current;

	countdown = new CountDown({
			props: { countDown: /*countDown*/ ctx[4] },
			$$inline: true
		});

	const block = {
		c: function create() {
			div = element("div");
			create_component(countdown.$$.fragment);
			attr_dev(div, "class", "proton-link-info");
			add_location(div, file, 55, 16, 1620);
		},
		m: function mount(target, anchor) {
			insert_dev(target, div, anchor);
			mount_component(countdown, div, null);
			current = true;
		},
		p: function update(ctx, dirty) {
			const countdown_changes = {};
			if (dirty & /*countDown*/ 16) countdown_changes.countDown = /*countDown*/ ctx[4];
			countdown.$set(countdown_changes);
		},
		i: function intro(local) {
			if (current) return;
			transition_in(countdown.$$.fragment, local);
			current = true;
		},
		o: function outro(local) {
			transition_out(countdown.$$.fragment, local);
			current = false;
		},
		d: function destroy(detaching) {
			if (detaching) detach_dev(div);
			destroy_component(countdown);
		}
	};

	dispatch_dev("SvelteRegisterBlock", {
		block,
		id: create_if_block_2.name,
		type: "if",
		source: "(55:12) {#if countDown}",
		ctx
	});

	return block;
}

// (61:12) {#if action}
function create_if_block_1(ctx) {
	let hr;
	let t0;
	let button;
	let t1_value = /*action*/ ctx[6].text + "";
	let t1;
	let mounted;
	let dispose;

	const block = {
		c: function create() {
			hr = element("hr");
			t0 = space();
			button = element("button");
			t1 = text(t1_value);
			attr_dev(hr, "class", "proton-link-hr");
			add_location(hr, file, 61, 16, 1780);
			attr_dev(button, "class", "proton-link-button");
			add_location(button, file, 62, 16, 1826);
		},
		m: function mount(target, anchor) {
			insert_dev(target, hr, anchor);
			insert_dev(target, t0, anchor);
			insert_dev(target, button, anchor);
			append_dev(button, t1);

			if (!mounted) {
				dispose = listen_dev(button, "click", /*doAction*/ ctx[11], false, false, false);
				mounted = true;
			}
		},
		p: function update(ctx, dirty) {
			if (dirty & /*action*/ 64 && t1_value !== (t1_value = /*action*/ ctx[6].text + "")) set_data_dev(t1, t1_value);
		},
		d: function destroy(detaching) {
			if (detaching) detach_dev(hr);
			if (detaching) detach_dev(t0);
			if (detaching) detach_dev(button);
			mounted = false;
			dispose();
		}
	};

	dispatch_dev("SvelteRegisterBlock", {
		block,
		id: create_if_block_1.name,
		type: "if",
		source: "(61:12) {#if action}",
		ctx
	});

	return block;
}

// (66:12) {#if footnoteLink}
function create_if_block(ctx) {
	let div;
	let t0;
	let a;
	let t1;

	const block = {
		c: function create() {
			div = element("div");
			t0 = text("Don't have a wallet? ");
			a = element("a");
			t1 = text("Download it here");
			attr_dev(a, "href", /*footnoteLink*/ ctx[7]);
			attr_dev(a, "target", "_blank");
			attr_dev(a, "rel", "noreferrer");
			add_location(a, file, 67, 41, 2046);
			attr_dev(div, "class", "proton-link-footnote");
			add_location(div, file, 66, 16, 1970);
		},
		m: function mount(target, anchor) {
			insert_dev(target, div, anchor);
			append_dev(div, t0);
			append_dev(div, a);
			append_dev(a, t1);
		},
		p: function update(ctx, dirty) {
			if (dirty & /*footnoteLink*/ 128) {
				attr_dev(a, "href", /*footnoteLink*/ ctx[7]);
			}
		},
		d: function destroy(detaching) {
			if (detaching) detach_dev(div);
		}
	};

	dispatch_dev("SvelteRegisterBlock", {
		block,
		id: create_if_block.name,
		type: "if",
		source: "(66:12) {#if footnoteLink}",
		ctx
	});

	return block;
}

function create_fragment(ctx) {
	let div4;
	let div3;
	let div1;
	let t0;
	let span;
	let t1;
	let t2;
	let div0;
	let t3;
	let div2;
	let t4;
	let t5;
	let t6;
	let t7;
	let div4_class_value;
	let current;
	let mounted;
	let dispose;
	let if_block0 = /*showBackButton*/ ctx[1] && create_if_block_5(ctx);
	let if_block1 = /*subtitle*/ ctx[3] && create_if_block_4(ctx);
	let if_block2 = /*qrData*/ ctx[5] && create_if_block_3(ctx);
	let if_block3 = /*countDown*/ ctx[4] && create_if_block_2(ctx);
	let if_block4 = /*action*/ ctx[6] && create_if_block_1(ctx);
	let if_block5 = /*footnoteLink*/ ctx[7] && create_if_block(ctx);

	const block = {
		c: function create() {
			div4 = element("div");
			div3 = element("div");
			div1 = element("div");
			if (if_block0) if_block0.c();
			t0 = space();
			span = element("span");
			t1 = text(/*title*/ ctx[2]);
			t2 = space();
			div0 = element("div");
			t3 = space();
			div2 = element("div");
			if (if_block1) if_block1.c();
			t4 = space();
			if (if_block2) if_block2.c();
			t5 = space();
			if (if_block3) if_block3.c();
			t6 = space();
			if (if_block4) if_block4.c();
			t7 = space();
			if (if_block5) if_block5.c();
			attr_dev(span, "class", "proton-link-header");
			add_location(span, file, 42, 12, 1210);
			attr_dev(div0, "class", "proton-link-close");
			add_location(div0, file, 43, 12, 1270);
			attr_dev(div1, "class", "proton-link-nav");
			add_location(div1, file, 38, 8, 1052);
			attr_dev(div2, "class", "proton-link-request");
			add_location(div2, file, 45, 8, 1344);
			attr_dev(div3, "class", "proton-link-inner");
			add_location(div3, file, 37, 4, 1012);
			attr_dev(div4, "class", div4_class_value = "proton-link" + /*walletTypeClass*/ ctx[8]);
			toggle_class(div4, "is-active", /*show*/ ctx[0]);
			add_location(div4, file, 32, 0, 891);
		},
		l: function claim(nodes) {
			throw new Error("options.hydrate only works if the component was compiled with the `hydratable: true` option");
		},
		m: function mount(target, anchor) {
			insert_dev(target, div4, anchor);
			append_dev(div4, div3);
			append_dev(div3, div1);
			if (if_block0) if_block0.m(div1, null);
			append_dev(div1, t0);
			append_dev(div1, span);
			append_dev(span, t1);
			append_dev(div1, t2);
			append_dev(div1, div0);
			append_dev(div3, t3);
			append_dev(div3, div2);
			if (if_block1) if_block1.m(div2, null);
			append_dev(div2, t4);
			if (if_block2) if_block2.m(div2, null);
			append_dev(div2, t5);
			if (if_block3) if_block3.m(div2, null);
			append_dev(div2, t6);
			if (if_block4) if_block4.m(div2, null);
			append_dev(div2, t7);
			if (if_block5) if_block5.m(div2, null);
			current = true;

			if (!mounted) {
				dispose = [
					listen_dev(div0, "click", /*close*/ ctx[10], false, false, false),
					listen_dev(div4, "click", self(stop_propagation(/*close*/ ctx[10])), false, false, true)
				];

				mounted = true;
			}
		},
		p: function update(ctx, [dirty]) {
			if (/*showBackButton*/ ctx[1]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);
				} else {
					if_block0 = create_if_block_5(ctx);
					if_block0.c();
					if_block0.m(div1, t0);
				}
			} else if (if_block0) {
				if_block0.d(1);
				if_block0 = null;
			}

			if (!current || dirty & /*title*/ 4) set_data_dev(t1, /*title*/ ctx[2]);

			if (/*subtitle*/ ctx[3]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);
				} else {
					if_block1 = create_if_block_4(ctx);
					if_block1.c();
					if_block1.m(div2, t4);
				}
			} else if (if_block1) {
				if_block1.d(1);
				if_block1 = null;
			}

			if (/*qrData*/ ctx[5]) {
				if (if_block2) {
					if_block2.p(ctx, dirty);

					if (dirty & /*qrData*/ 32) {
						transition_in(if_block2, 1);
					}
				} else {
					if_block2 = create_if_block_3(ctx);
					if_block2.c();
					transition_in(if_block2, 1);
					if_block2.m(div2, t5);
				}
			} else if (if_block2) {
				group_outros();

				transition_out(if_block2, 1, 1, () => {
					if_block2 = null;
				});

				check_outros();
			}

			if (/*countDown*/ ctx[4]) {
				if (if_block3) {
					if_block3.p(ctx, dirty);

					if (dirty & /*countDown*/ 16) {
						transition_in(if_block3, 1);
					}
				} else {
					if_block3 = create_if_block_2(ctx);
					if_block3.c();
					transition_in(if_block3, 1);
					if_block3.m(div2, t6);
				}
			} else if (if_block3) {
				group_outros();

				transition_out(if_block3, 1, 1, () => {
					if_block3 = null;
				});

				check_outros();
			}

			if (/*action*/ ctx[6]) {
				if (if_block4) {
					if_block4.p(ctx, dirty);
				} else {
					if_block4 = create_if_block_1(ctx);
					if_block4.c();
					if_block4.m(div2, t7);
				}
			} else if (if_block4) {
				if_block4.d(1);
				if_block4 = null;
			}

			if (/*footnoteLink*/ ctx[7]) {
				if (if_block5) {
					if_block5.p(ctx, dirty);
				} else {
					if_block5 = create_if_block(ctx);
					if_block5.c();
					if_block5.m(div2, null);
				}
			} else if (if_block5) {
				if_block5.d(1);
				if_block5 = null;
			}

			if (!current || dirty & /*walletTypeClass*/ 256 && div4_class_value !== (div4_class_value = "proton-link" + /*walletTypeClass*/ ctx[8])) {
				attr_dev(div4, "class", div4_class_value);
			}

			if (dirty & /*walletTypeClass, show*/ 257) {
				toggle_class(div4, "is-active", /*show*/ ctx[0]);
			}
		},
		i: function intro(local) {
			if (current) return;
			transition_in(if_block2);
			transition_in(if_block3);
			current = true;
		},
		o: function outro(local) {
			transition_out(if_block2);
			transition_out(if_block3);
			current = false;
		},
		d: function destroy(detaching) {
			if (detaching) detach_dev(div4);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
			if (if_block2) if_block2.d();
			if (if_block3) if_block3.d();
			if (if_block4) if_block4.d();
			if (if_block5) if_block5.d();
			mounted = false;
			run_all(dispose);
		}
	};

	dispatch_dev("SvelteRegisterBlock", {
		block,
		id: create_fragment.name,
		type: "component",
		source: "",
		ctx
	});

	return block;
}

function instance($$self, $$props, $$invalidate) {
	let walletTypeClass;
	let footnoteLink;
	let { $$slots: slots = {}, $$scope } = $$props;
	validate_slots('Dialog', slots, []);
	const dispatch = createEventDispatcher();
	let { show = false } = $$props;
	let { showBackButton = false } = $$props;
	let { walletType = 'proton' } = $$props;
	let { title = '' } = $$props;
	let { subtitle = null } = $$props;
	let { showFootnote = false } = $$props;
	let { countDown = null } = $$props;
	let { qrData = null } = $$props;
	let { action = null } = $$props;

	function back() {
		dispatch('back');
		close();
	}

	function close() {
		$$invalidate(0, show = false);
		dispatch('close');
	}

	function doAction() {
		if (action && action.callback) {
			action.callback();
		}
	}

	const writable_props = [
		'show',
		'showBackButton',
		'walletType',
		'title',
		'subtitle',
		'showFootnote',
		'countDown',
		'qrData',
		'action'
	];

	Object.keys($$props).forEach(key => {
		if (!~writable_props.indexOf(key) && key.slice(0, 2) !== '$$' && key !== 'slot') console.warn(`<Dialog> was created with unknown prop '${key}'`);
	});

	$$self.$$set = $$props => {
		if ('show' in $$props) $$invalidate(0, show = $$props.show);
		if ('showBackButton' in $$props) $$invalidate(1, showBackButton = $$props.showBackButton);
		if ('walletType' in $$props) $$invalidate(12, walletType = $$props.walletType);
		if ('title' in $$props) $$invalidate(2, title = $$props.title);
		if ('subtitle' in $$props) $$invalidate(3, subtitle = $$props.subtitle);
		if ('showFootnote' in $$props) $$invalidate(13, showFootnote = $$props.showFootnote);
		if ('countDown' in $$props) $$invalidate(4, countDown = $$props.countDown);
		if ('qrData' in $$props) $$invalidate(5, qrData = $$props.qrData);
		if ('action' in $$props) $$invalidate(6, action = $$props.action);
	};

	$$self.$capture_state = () => ({
		createEventDispatcher,
		getFootnoteLink,
		CountDown,
		QrCode: QRCode$1,
		dispatch,
		show,
		showBackButton,
		walletType,
		title,
		subtitle,
		showFootnote,
		countDown,
		qrData,
		action,
		back,
		close,
		doAction,
		footnoteLink,
		walletTypeClass
	});

	$$self.$inject_state = $$props => {
		if ('show' in $$props) $$invalidate(0, show = $$props.show);
		if ('showBackButton' in $$props) $$invalidate(1, showBackButton = $$props.showBackButton);
		if ('walletType' in $$props) $$invalidate(12, walletType = $$props.walletType);
		if ('title' in $$props) $$invalidate(2, title = $$props.title);
		if ('subtitle' in $$props) $$invalidate(3, subtitle = $$props.subtitle);
		if ('showFootnote' in $$props) $$invalidate(13, showFootnote = $$props.showFootnote);
		if ('countDown' in $$props) $$invalidate(4, countDown = $$props.countDown);
		if ('qrData' in $$props) $$invalidate(5, qrData = $$props.qrData);
		if ('action' in $$props) $$invalidate(6, action = $$props.action);
		if ('footnoteLink' in $$props) $$invalidate(7, footnoteLink = $$props.footnoteLink);
		if ('walletTypeClass' in $$props) $$invalidate(8, walletTypeClass = $$props.walletTypeClass);
	};

	if ($$props && "$$inject" in $$props) {
		$$self.$inject_state($$props.$$inject);
	}

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*walletType*/ 4096) {
			$$invalidate(8, walletTypeClass = ` proton-link--${walletType}`);
		}

		if ($$self.$$.dirty & /*showFootnote, walletType*/ 12288) {
			$$invalidate(7, footnoteLink = showFootnote ? getFootnoteLink(walletType) : '');
		}
	};

	return [
		show,
		showBackButton,
		title,
		subtitle,
		countDown,
		qrData,
		action,
		footnoteLink,
		walletTypeClass,
		back,
		close,
		doAction,
		walletType,
		showFootnote
	];
}

class Dialog extends SvelteComponentDev {
	constructor(options) {
		super(options);

		init(
			this,
			options,
			instance,
			create_fragment,
			safe_not_equal,
			{
				show: 0,
				showBackButton: 1,
				walletType: 12,
				title: 2,
				subtitle: 3,
				showFootnote: 13,
				countDown: 4,
				qrData: 5,
				action: 6
			},
			add_css
		);

		dispatch_dev("SvelteRegisterComponent", {
			component: this,
			tagName: "Dialog",
			options,
			id: create_fragment.name
		});
	}

	get show() {
		throw new Error("<Dialog>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set show(value) {
		throw new Error("<Dialog>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	get showBackButton() {
		throw new Error("<Dialog>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set showBackButton(value) {
		throw new Error("<Dialog>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	get walletType() {
		throw new Error("<Dialog>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set walletType(value) {
		throw new Error("<Dialog>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	get title() {
		throw new Error("<Dialog>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set title(value) {
		throw new Error("<Dialog>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	get subtitle() {
		throw new Error("<Dialog>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set subtitle(value) {
		throw new Error("<Dialog>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	get showFootnote() {
		throw new Error("<Dialog>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set showFootnote(value) {
		throw new Error("<Dialog>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	get countDown() {
		throw new Error("<Dialog>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set countDown(value) {
		throw new Error("<Dialog>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	get qrData() {
		throw new Error("<Dialog>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set qrData(value) {
		throw new Error("<Dialog>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	get action() {
		throw new Error("<Dialog>: Props cannot be read directly from the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}

	set action(value) {
		throw new Error("<Dialog>: Props cannot be set directly on the component instance unless compiling with 'accessors: true' or '<svelte:options accessors/>'");
	}
}

class Storage {
    constructor(keyPrefix) {
        this.keyPrefix = keyPrefix;
    }
    write(key, data) {
        return __awaiter(this, void 0, void 0, function* () {
            localStorage.setItem(this.storageKey(key), data);
        });
    }
    read(key) {
        return __awaiter(this, void 0, void 0, function* () {
            return localStorage.getItem(this.storageKey(key));
        });
    }
    remove(key) {
        return __awaiter(this, void 0, void 0, function* () {
            localStorage.removeItem(this.storageKey(key));
        });
    }
    storageKey(key) {
        return `${this.keyPrefix}-${key}`;
    }
}

const SkipToManual = Symbol();

var ErrorCorrectLevel = {
    L: 1,
    M: 0,
    Q: 3,
    H: 2,
};

var Mode = {
    MODE_NUMBER: 1 << 0,
    MODE_ALPHA_NUM: 1 << 1,
    MODE_8BIT_BYTE: 1 << 2,
    MODE_KANJI: 1 << 3,
};

class QR8bitByte {
    constructor(data) {
        this.mode = Mode.MODE_8BIT_BYTE;
        this.data = data;
    }
    getLength() {
        return this.data.length;
    }
    write(buffer) {
        for (let i = 0; i < this.data.length; i++) {
            // not JIS ...
            buffer.put(this.data.charCodeAt(i), 8);
        }
    }
}

class QRBitBuffer {
    constructor() {
        this.buffer = [];
        this.length = 0;
    }
    get(index) {
        const bufIndex = Math.floor(index / 8);
        return ((this.buffer[bufIndex] >>> (7 - (index % 8))) & 1) == 1;
    }
    put(num, length) {
        for (let i = 0; i < length; i++) {
            this.putBit(((num >>> (length - i - 1)) & 1) == 1);
        }
    }
    getLengthInBits() {
        return this.length;
    }
    putBit(bit) {
        const bufIndex = Math.floor(this.length / 8);
        if (this.buffer.length <= bufIndex) {
            this.buffer.push(0);
        }
        if (bit) {
            this.buffer[bufIndex] |= 0x80 >>> this.length % 8;
        }
        this.length++;
    }
}

const QRMath = {
    glog: function (n) {
        if (n < 1) {
            throw new Error('glog(' + n + ')');
        }
        return QRMath.LOG_TABLE[n];
    },
    gexp: function (n) {
        while (n < 0) {
            n += 255;
        }
        while (n >= 256) {
            n -= 255;
        }
        return QRMath.EXP_TABLE[n];
    },
    EXP_TABLE: new Array(256),
    LOG_TABLE: new Array(256),
};
for (let i = 0; i < 8; i++) {
    QRMath.EXP_TABLE[i] = 1 << i;
}
for (let i = 8; i < 256; i++) {
    QRMath.EXP_TABLE[i] =
        QRMath.EXP_TABLE[i - 4] ^
            QRMath.EXP_TABLE[i - 5] ^
            QRMath.EXP_TABLE[i - 6] ^
            QRMath.EXP_TABLE[i - 8];
}
for (let i = 0; i < 255; i++) {
    QRMath.LOG_TABLE[QRMath.EXP_TABLE[i]] = i;
}

class QRPolynomial {
    constructor(num, shift) {
        if (num.length == undefined) {
            throw new Error(num.length + '/' + shift);
        }
        let offset = 0;
        while (offset < num.length && num[offset] == 0) {
            offset++;
        }
        this.num = new Array(num.length - offset + shift);
        for (let i = 0; i < num.length - offset; i++) {
            this.num[i] = num[i + offset];
        }
    }
    get(index) {
        return this.num[index];
    }
    getLength() {
        return this.num.length;
    }
    multiply(e) {
        const num = new Array(this.getLength() + e.getLength() - 1);
        for (let i = 0; i < this.getLength(); i++) {
            for (let j = 0; j < e.getLength(); j++) {
                num[i + j] ^= QRMath.gexp(QRMath.glog(this.get(i)) + QRMath.glog(e.get(j)));
            }
        }
        return new QRPolynomial(num, 0);
    }
    mod(e) {
        if (this.getLength() - e.getLength() < 0) {
            return this;
        }
        const ratio = QRMath.glog(this.get(0)) - QRMath.glog(e.get(0));
        const num = new Array(this.getLength());
        for (let i = 0; i < this.getLength(); i++) {
            num[i] = this.get(i);
        }
        for (let i = 0; i < e.getLength(); i++) {
            num[i] ^= QRMath.gexp(QRMath.glog(e.get(i)) + ratio);
        }
        // recursive call
        return new QRPolynomial(num, 0).mod(e);
    }
}

// ErrorCorrectLevel
class QRRSBlock {
    constructor(totalCount, dataCount) {
        this.totalCount = totalCount;
        this.dataCount = dataCount;
    }
    static getRSBlocks(typeNumber, errorCorrectLevel) {
        const rsBlock = QRRSBlock.getRsBlockTable(typeNumber, errorCorrectLevel);
        if (rsBlock == undefined) {
            throw new Error('bad rs block @ typeNumber:' +
                typeNumber +
                '/errorCorrectLevel:' +
                errorCorrectLevel);
        }
        const length = rsBlock.length / 3;
        const list = [];
        for (let i = 0; i < length; i++) {
            const count = rsBlock[i * 3 + 0];
            const totalCount = rsBlock[i * 3 + 1];
            const dataCount = rsBlock[i * 3 + 2];
            for (let j = 0; j < count; j++) {
                list.push(new QRRSBlock(totalCount, dataCount));
            }
        }
        return list;
    }
    static getRsBlockTable(typeNumber, errorCorrectLevel) {
        switch (errorCorrectLevel) {
            case ErrorCorrectLevel.L:
                return QRRSBlock.RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 0];
            case ErrorCorrectLevel.M:
                return QRRSBlock.RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 1];
            case ErrorCorrectLevel.Q:
                return QRRSBlock.RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 2];
            case ErrorCorrectLevel.H:
                return QRRSBlock.RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 3];
            default:
                return undefined;
        }
    }
}
QRRSBlock.RS_BLOCK_TABLE = [
    // L
    // M
    // Q
    // H
    // 1
    [1, 26, 19],
    [1, 26, 16],
    [1, 26, 13],
    [1, 26, 9],
    // 2
    [1, 44, 34],
    [1, 44, 28],
    [1, 44, 22],
    [1, 44, 16],
    // 3
    [1, 70, 55],
    [1, 70, 44],
    [2, 35, 17],
    [2, 35, 13],
    // 4
    [1, 100, 80],
    [2, 50, 32],
    [2, 50, 24],
    [4, 25, 9],
    // 5
    [1, 134, 108],
    [2, 67, 43],
    [2, 33, 15, 2, 34, 16],
    [2, 33, 11, 2, 34, 12],
    // 6
    [2, 86, 68],
    [4, 43, 27],
    [4, 43, 19],
    [4, 43, 15],
    // 7
    [2, 98, 78],
    [4, 49, 31],
    [2, 32, 14, 4, 33, 15],
    [4, 39, 13, 1, 40, 14],
    // 8
    [2, 121, 97],
    [2, 60, 38, 2, 61, 39],
    [4, 40, 18, 2, 41, 19],
    [4, 40, 14, 2, 41, 15],
    // 9
    [2, 146, 116],
    [3, 58, 36, 2, 59, 37],
    [4, 36, 16, 4, 37, 17],
    [4, 36, 12, 4, 37, 13],
    // 10
    [2, 86, 68, 2, 87, 69],
    [4, 69, 43, 1, 70, 44],
    [6, 43, 19, 2, 44, 20],
    [6, 43, 15, 2, 44, 16],
    // 11
    [4, 101, 81],
    [1, 80, 50, 4, 81, 51],
    [4, 50, 22, 4, 51, 23],
    [3, 36, 12, 8, 37, 13],
    // 12
    [2, 116, 92, 2, 117, 93],
    [6, 58, 36, 2, 59, 37],
    [4, 46, 20, 6, 47, 21],
    [7, 42, 14, 4, 43, 15],
    // 13
    [4, 133, 107],
    [8, 59, 37, 1, 60, 38],
    [8, 44, 20, 4, 45, 21],
    [12, 33, 11, 4, 34, 12],
    // 14
    [3, 145, 115, 1, 146, 116],
    [4, 64, 40, 5, 65, 41],
    [11, 36, 16, 5, 37, 17],
    [11, 36, 12, 5, 37, 13],
    // 15
    [5, 109, 87, 1, 110, 88],
    [5, 65, 41, 5, 66, 42],
    [5, 54, 24, 7, 55, 25],
    [11, 36, 12],
    // 16
    [5, 122, 98, 1, 123, 99],
    [7, 73, 45, 3, 74, 46],
    [15, 43, 19, 2, 44, 20],
    [3, 45, 15, 13, 46, 16],
    // 17
    [1, 135, 107, 5, 136, 108],
    [10, 74, 46, 1, 75, 47],
    [1, 50, 22, 15, 51, 23],
    [2, 42, 14, 17, 43, 15],
    // 18
    [5, 150, 120, 1, 151, 121],
    [9, 69, 43, 4, 70, 44],
    [17, 50, 22, 1, 51, 23],
    [2, 42, 14, 19, 43, 15],
    // 19
    [3, 141, 113, 4, 142, 114],
    [3, 70, 44, 11, 71, 45],
    [17, 47, 21, 4, 48, 22],
    [9, 39, 13, 16, 40, 14],
    // 20
    [3, 135, 107, 5, 136, 108],
    [3, 67, 41, 13, 68, 42],
    [15, 54, 24, 5, 55, 25],
    [15, 43, 15, 10, 44, 16],
    // 21
    [4, 144, 116, 4, 145, 117],
    [17, 68, 42],
    [17, 50, 22, 6, 51, 23],
    [19, 46, 16, 6, 47, 17],
    // 22
    [2, 139, 111, 7, 140, 112],
    [17, 74, 46],
    [7, 54, 24, 16, 55, 25],
    [34, 37, 13],
    // 23
    [4, 151, 121, 5, 152, 122],
    [4, 75, 47, 14, 76, 48],
    [11, 54, 24, 14, 55, 25],
    [16, 45, 15, 14, 46, 16],
    // 24
    [6, 147, 117, 4, 148, 118],
    [6, 73, 45, 14, 74, 46],
    [11, 54, 24, 16, 55, 25],
    [30, 46, 16, 2, 47, 17],
    // 25
    [8, 132, 106, 4, 133, 107],
    [8, 75, 47, 13, 76, 48],
    [7, 54, 24, 22, 55, 25],
    [22, 45, 15, 13, 46, 16],
    // 26
    [10, 142, 114, 2, 143, 115],
    [19, 74, 46, 4, 75, 47],
    [28, 50, 22, 6, 51, 23],
    [33, 46, 16, 4, 47, 17],
    // 27
    [8, 152, 122, 4, 153, 123],
    [22, 73, 45, 3, 74, 46],
    [8, 53, 23, 26, 54, 24],
    [12, 45, 15, 28, 46, 16],
    // 28
    [3, 147, 117, 10, 148, 118],
    [3, 73, 45, 23, 74, 46],
    [4, 54, 24, 31, 55, 25],
    [11, 45, 15, 31, 46, 16],
    // 29
    [7, 146, 116, 7, 147, 117],
    [21, 73, 45, 7, 74, 46],
    [1, 53, 23, 37, 54, 24],
    [19, 45, 15, 26, 46, 16],
    // 30
    [5, 145, 115, 10, 146, 116],
    [19, 75, 47, 10, 76, 48],
    [15, 54, 24, 25, 55, 25],
    [23, 45, 15, 25, 46, 16],
    // 31
    [13, 145, 115, 3, 146, 116],
    [2, 74, 46, 29, 75, 47],
    [42, 54, 24, 1, 55, 25],
    [23, 45, 15, 28, 46, 16],
    // 32
    [17, 145, 115],
    [10, 74, 46, 23, 75, 47],
    [10, 54, 24, 35, 55, 25],
    [19, 45, 15, 35, 46, 16],
    // 33
    [17, 145, 115, 1, 146, 116],
    [14, 74, 46, 21, 75, 47],
    [29, 54, 24, 19, 55, 25],
    [11, 45, 15, 46, 46, 16],
    // 34
    [13, 145, 115, 6, 146, 116],
    [14, 74, 46, 23, 75, 47],
    [44, 54, 24, 7, 55, 25],
    [59, 46, 16, 1, 47, 17],
    // 35
    [12, 151, 121, 7, 152, 122],
    [12, 75, 47, 26, 76, 48],
    [39, 54, 24, 14, 55, 25],
    [22, 45, 15, 41, 46, 16],
    // 36
    [6, 151, 121, 14, 152, 122],
    [6, 75, 47, 34, 76, 48],
    [46, 54, 24, 10, 55, 25],
    [2, 45, 15, 64, 46, 16],
    // 37
    [17, 152, 122, 4, 153, 123],
    [29, 74, 46, 14, 75, 47],
    [49, 54, 24, 10, 55, 25],
    [24, 45, 15, 46, 46, 16],
    // 38
    [4, 152, 122, 18, 153, 123],
    [13, 74, 46, 32, 75, 47],
    [48, 54, 24, 14, 55, 25],
    [42, 45, 15, 32, 46, 16],
    // 39
    [20, 147, 117, 4, 148, 118],
    [40, 75, 47, 7, 76, 48],
    [43, 54, 24, 22, 55, 25],
    [10, 45, 15, 67, 46, 16],
    // 40
    [19, 148, 118, 6, 149, 119],
    [18, 75, 47, 31, 76, 48],
    [34, 54, 24, 34, 55, 25],
    [20, 45, 15, 61, 46, 16],
];

const QRMaskPattern = {
    PATTERN000: 0,
    PATTERN001: 1,
    PATTERN010: 2,
    PATTERN011: 3,
    PATTERN100: 4,
    PATTERN101: 5,
    PATTERN110: 6,
    PATTERN111: 7,
};
const QRUtil = {
    PATTERN_POSITION_TABLE: [
        [],
        [6, 18],
        [6, 22],
        [6, 26],
        [6, 30],
        [6, 34],
        [6, 22, 38],
        [6, 24, 42],
        [6, 26, 46],
        [6, 28, 50],
        [6, 30, 54],
        [6, 32, 58],
        [6, 34, 62],
        [6, 26, 46, 66],
        [6, 26, 48, 70],
        [6, 26, 50, 74],
        [6, 30, 54, 78],
        [6, 30, 56, 82],
        [6, 30, 58, 86],
        [6, 34, 62, 90],
        [6, 28, 50, 72, 94],
        [6, 26, 50, 74, 98],
        [6, 30, 54, 78, 102],
        [6, 28, 54, 80, 106],
        [6, 32, 58, 84, 110],
        [6, 30, 58, 86, 114],
        [6, 34, 62, 90, 118],
        [6, 26, 50, 74, 98, 122],
        [6, 30, 54, 78, 102, 126],
        [6, 26, 52, 78, 104, 130],
        [6, 30, 56, 82, 108, 134],
        [6, 34, 60, 86, 112, 138],
        [6, 30, 58, 86, 114, 142],
        [6, 34, 62, 90, 118, 146],
        [6, 30, 54, 78, 102, 126, 150],
        [6, 24, 50, 76, 102, 128, 154],
        [6, 28, 54, 80, 106, 132, 158],
        [6, 32, 58, 84, 110, 136, 162],
        [6, 26, 54, 82, 110, 138, 166],
        [6, 30, 58, 86, 114, 142, 170],
    ],
    G15: (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | (1 << 0),
    G18: (1 << 12) | (1 << 11) | (1 << 10) | (1 << 9) | (1 << 8) | (1 << 5) | (1 << 2) | (1 << 0),
    G15_MASK: (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1),
    getBCHTypeInfo: function (data) {
        let d = data << 10;
        while (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G15) >= 0) {
            d ^= QRUtil.G15 << (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G15));
        }
        return ((data << 10) | d) ^ QRUtil.G15_MASK;
    },
    getBCHTypeNumber: function (data) {
        let d = data << 12;
        while (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G18) >= 0) {
            d ^= QRUtil.G18 << (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G18));
        }
        return (data << 12) | d;
    },
    getBCHDigit: function (data) {
        let digit = 0;
        while (data != 0) {
            digit++;
            data >>>= 1;
        }
        return digit;
    },
    getPatternPosition: function (typeNumber) {
        return QRUtil.PATTERN_POSITION_TABLE[typeNumber - 1];
    },
    getMask: function (maskPattern, i, j) {
        switch (maskPattern) {
            case QRMaskPattern.PATTERN000:
                return (i + j) % 2 == 0;
            case QRMaskPattern.PATTERN001:
                return i % 2 == 0;
            case QRMaskPattern.PATTERN010:
                return j % 3 == 0;
            case QRMaskPattern.PATTERN011:
                return (i + j) % 3 == 0;
            case QRMaskPattern.PATTERN100:
                return (Math.floor(i / 2) + Math.floor(j / 3)) % 2 == 0;
            case QRMaskPattern.PATTERN101:
                return ((i * j) % 2) + ((i * j) % 3) == 0;
            case QRMaskPattern.PATTERN110:
                return (((i * j) % 2) + ((i * j) % 3)) % 2 == 0;
            case QRMaskPattern.PATTERN111:
                return (((i * j) % 3) + ((i + j) % 2)) % 2 == 0;
            default:
                throw new Error('bad maskPattern:' + maskPattern);
        }
    },
    getErrorCorrectPolynomial: function (errorCorrectLength) {
        let a = new QRPolynomial([1], 0);
        for (let i = 0; i < errorCorrectLength; i++) {
            a = a.multiply(new QRPolynomial([1, QRMath.gexp(i)], 0));
        }
        return a;
    },
    getLengthInBits: function (mode, type) {
        if (1 <= type && type < 10) {
            // 1 - 9
            switch (mode) {
                case Mode.MODE_NUMBER:
                    return 10;
                case Mode.MODE_ALPHA_NUM:
                    return 9;
                case Mode.MODE_8BIT_BYTE:
                    return 8;
                case Mode.MODE_KANJI:
                    return 8;
                default:
                    throw new Error('mode:' + mode);
            }
        }
        else if (type < 27) {
            // 10 - 26
            switch (mode) {
                case Mode.MODE_NUMBER:
                    return 12;
                case Mode.MODE_ALPHA_NUM:
                    return 11;
                case Mode.MODE_8BIT_BYTE:
                    return 16;
                case Mode.MODE_KANJI:
                    return 10;
                default:
                    throw new Error('mode:' + mode);
            }
        }
        else if (type < 41) {
            // 27 - 40
            switch (mode) {
                case Mode.MODE_NUMBER:
                    return 14;
                case Mode.MODE_ALPHA_NUM:
                    return 13;
                case Mode.MODE_8BIT_BYTE:
                    return 16;
                case Mode.MODE_KANJI:
                    return 12;
                default:
                    throw new Error('mode:' + mode);
            }
        }
        else {
            throw new Error('type:' + type);
        }
    },
    getLostPoint: function (qrCode) {
        const moduleCount = qrCode.getModuleCount();
        let lostPoint = 0;
        // LEVEL1
        for (let row = 0; row < moduleCount; row++) {
            for (let col = 0; col < moduleCount; col++) {
                let sameCount = 0;
                const dark = qrCode.isDark(row, col);
                for (let r = -1; r <= 1; r++) {
                    if (row + r < 0 || moduleCount <= row + r) {
                        continue;
                    }
                    for (let c = -1; c <= 1; c++) {
                        if (col + c < 0 || moduleCount <= col + c) {
                            continue;
                        }
                        if (r == 0 && c == 0) {
                            continue;
                        }
                        if (dark == qrCode.isDark(row + r, col + c)) {
                            sameCount++;
                        }
                    }
                }
                if (sameCount > 5) {
                    lostPoint += 3 + sameCount - 5;
                }
            }
        }
        // LEVEL2
        for (let row = 0; row < moduleCount - 1; row++) {
            for (let col = 0; col < moduleCount - 1; col++) {
                let count = 0;
                if (qrCode.isDark(row, col))
                    count++;
                if (qrCode.isDark(row + 1, col))
                    count++;
                if (qrCode.isDark(row, col + 1))
                    count++;
                if (qrCode.isDark(row + 1, col + 1))
                    count++;
                if (count == 0 || count == 4) {
                    lostPoint += 3;
                }
            }
        }
        // LEVEL3
        for (let row = 0; row < moduleCount; row++) {
            for (let col = 0; col < moduleCount - 6; col++) {
                if (qrCode.isDark(row, col) &&
                    !qrCode.isDark(row, col + 1) &&
                    qrCode.isDark(row, col + 2) &&
                    qrCode.isDark(row, col + 3) &&
                    qrCode.isDark(row, col + 4) &&
                    !qrCode.isDark(row, col + 5) &&
                    qrCode.isDark(row, col + 6)) {
                    lostPoint += 40;
                }
            }
        }
        for (let col = 0; col < moduleCount; col++) {
            for (let row = 0; row < moduleCount - 6; row++) {
                if (qrCode.isDark(row, col) &&
                    !qrCode.isDark(row + 1, col) &&
                    qrCode.isDark(row + 2, col) &&
                    qrCode.isDark(row + 3, col) &&
                    qrCode.isDark(row + 4, col) &&
                    !qrCode.isDark(row + 5, col) &&
                    qrCode.isDark(row + 6, col)) {
                    lostPoint += 40;
                }
            }
        }
        // LEVEL4
        let darkCount = 0;
        for (let col = 0; col < moduleCount; col++) {
            for (let row = 0; row < moduleCount; row++) {
                if (qrCode.isDark(row, col)) {
                    darkCount++;
                }
            }
        }
        const ratio = Math.abs((100 * darkCount) / moduleCount / moduleCount - 50) / 5;
        lostPoint += ratio * 10;
        return lostPoint;
    },
};

class QRCode {
    constructor(typeNumber, errorCorrectLevel) {
        this.typeNumber = typeNumber;
        this.errorCorrectLevel = errorCorrectLevel;
        this.modules = null;
        this.moduleCount = 0;
        this.dataCache = null;
        this.dataList = [];
    }
    addData(data) {
        const newData = new QR8bitByte(data);
        this.dataList.push(newData);
        this.dataCache = null;
    }
    isDark(row, col) {
        if (row < 0 || this.moduleCount <= row || col < 0 || this.moduleCount <= col) {
            throw new Error(row + ',' + col);
        }
        return this.modules[row][col];
    }
    getModuleCount() {
        return this.moduleCount;
    }
    make() {
        // Calculate automatically typeNumber if provided is < 1
        if (this.typeNumber < 1) {
            let typeNumber = 1;
            for (typeNumber = 1; typeNumber < 40; typeNumber++) {
                const rsBlocks = QRRSBlock.getRSBlocks(typeNumber, this.errorCorrectLevel);
                const buffer = new QRBitBuffer();
                let totalDataCount = 0;
                for (let i = 0; i < rsBlocks.length; i++) {
                    totalDataCount += rsBlocks[i].dataCount;
                }
                for (let i = 0; i < this.dataList.length; i++) {
                    const data = this.dataList[i];
                    buffer.put(data.mode, 4);
                    buffer.put(data.getLength(), QRUtil.getLengthInBits(data.mode, typeNumber));
                    data.write(buffer);
                }
                if (buffer.getLengthInBits() <= totalDataCount * 8)
                    break;
            }
            this.typeNumber = typeNumber;
        }
        this.makeImpl(false, this.getBestMaskPattern());
    }
    makeImpl(test, maskPattern) {
        this.moduleCount = this.typeNumber * 4 + 17;
        this.modules = new Array(this.moduleCount);
        for (let row = 0; row < this.moduleCount; row++) {
            this.modules[row] = new Array(this.moduleCount);
            for (let col = 0; col < this.moduleCount; col++) {
                this.modules[row][col] = null; //(col + row) % 3;
            }
        }
        this.setupPositionProbePattern(0, 0);
        this.setupPositionProbePattern(this.moduleCount - 7, 0);
        this.setupPositionProbePattern(0, this.moduleCount - 7);
        this.setupPositionAdjustPattern();
        this.setupTimingPattern();
        this.setupTypeInfo(test, maskPattern);
        if (this.typeNumber >= 7) {
            this.setupTypeNumber(test);
        }
        if (this.dataCache == null) {
            this.dataCache = QRCode.createData(this.typeNumber, this.errorCorrectLevel, this.dataList);
        }
        this.mapData(this.dataCache, maskPattern);
    }
    setupPositionProbePattern(row, col) {
        for (let r = -1; r <= 7; r++) {
            if (row + r <= -1 || this.moduleCount <= row + r)
                continue;
            for (let c = -1; c <= 7; c++) {
                if (col + c <= -1 || this.moduleCount <= col + c)
                    continue;
                if ((0 <= r && r <= 6 && (c == 0 || c == 6)) ||
                    (0 <= c && c <= 6 && (r == 0 || r == 6)) ||
                    (2 <= r && r <= 4 && 2 <= c && c <= 4)) {
                    this.modules[row + r][col + c] = true;
                }
                else {
                    this.modules[row + r][col + c] = false;
                }
            }
        }
    }
    getBestMaskPattern() {
        let minLostPoint = 0;
        let pattern = 0;
        for (let i = 0; i < 8; i++) {
            this.makeImpl(true, i);
            const lostPoint = QRUtil.getLostPoint(this);
            if (i == 0 || minLostPoint > lostPoint) {
                minLostPoint = lostPoint;
                pattern = i;
            }
        }
        return pattern;
    }
    setupTimingPattern() {
        for (let r = 8; r < this.moduleCount - 8; r++) {
            if (this.modules[r][6] != null) {
                continue;
            }
            this.modules[r][6] = r % 2 == 0;
        }
        for (let c = 8; c < this.moduleCount - 8; c++) {
            if (this.modules[6][c] != null) {
                continue;
            }
            this.modules[6][c] = c % 2 == 0;
        }
    }
    setupPositionAdjustPattern() {
        const pos = QRUtil.getPatternPosition(this.typeNumber);
        for (let i = 0; i < pos.length; i++) {
            for (let j = 0; j < pos.length; j++) {
                const row = pos[i];
                const col = pos[j];
                if (this.modules[row][col] != null) {
                    continue;
                }
                for (let r = -2; r <= 2; r++) {
                    for (let c = -2; c <= 2; c++) {
                        if (r == -2 || r == 2 || c == -2 || c == 2 || (r == 0 && c == 0)) {
                            this.modules[row + r][col + c] = true;
                        }
                        else {
                            this.modules[row + r][col + c] = false;
                        }
                    }
                }
            }
        }
    }
    setupTypeNumber(test) {
        const bits = QRUtil.getBCHTypeNumber(this.typeNumber);
        for (let i = 0; i < 18; i++) {
            const mod = !test && ((bits >> i) & 1) == 1;
            this.modules[Math.floor(i / 3)][(i % 3) + this.moduleCount - 8 - 3] = mod;
        }
        for (let i = 0; i < 18; i++) {
            const mod = !test && ((bits >> i) & 1) == 1;
            this.modules[(i % 3) + this.moduleCount - 8 - 3][Math.floor(i / 3)] = mod;
        }
    }
    setupTypeInfo(test, maskPattern) {
        const data = (this.errorCorrectLevel << 3) | maskPattern;
        const bits = QRUtil.getBCHTypeInfo(data);
        // vertical
        for (let i = 0; i < 15; i++) {
            const mod = !test && ((bits >> i) & 1) == 1;
            if (i < 6) {
                this.modules[i][8] = mod;
            }
            else if (i < 8) {
                this.modules[i + 1][8] = mod;
            }
            else {
                this.modules[this.moduleCount - 15 + i][8] = mod;
            }
        }
        // horizontal
        for (let i = 0; i < 15; i++) {
            const mod = !test && ((bits >> i) & 1) == 1;
            if (i < 8) {
                this.modules[8][this.moduleCount - i - 1] = mod;
            }
            else if (i < 9) {
                this.modules[8][15 - i - 1 + 1] = mod;
            }
            else {
                this.modules[8][15 - i - 1] = mod;
            }
        }
        // fixed module
        this.modules[this.moduleCount - 8][8] = !test;
    }
    mapData(data, maskPattern) {
        let inc = -1;
        let row = this.moduleCount - 1;
        let bitIndex = 7;
        let byteIndex = 0;
        for (let col = this.moduleCount - 1; col > 0; col -= 2) {
            if (col == 6)
                col--;
            for (;;) {
                for (let c = 0; c < 2; c++) {
                    if (this.modules[row][col - c] == null) {
                        let dark = false;
                        if (byteIndex < data.length) {
                            dark = ((data[byteIndex] >>> bitIndex) & 1) == 1;
                        }
                        const mask = QRUtil.getMask(maskPattern, row, col - c);
                        if (mask) {
                            dark = !dark;
                        }
                        this.modules[row][col - c] = dark;
                        bitIndex--;
                        if (bitIndex == -1) {
                            byteIndex++;
                            bitIndex = 7;
                        }
                    }
                }
                row += inc;
                if (row < 0 || this.moduleCount <= row) {
                    row -= inc;
                    inc = -inc;
                    break;
                }
            }
        }
    }
    static createData(typeNumber, errorCorrectLevel, dataList) {
        const rsBlocks = QRRSBlock.getRSBlocks(typeNumber, errorCorrectLevel);
        const buffer = new QRBitBuffer();
        for (let i = 0; i < dataList.length; i++) {
            const data = dataList[i];
            buffer.put(data.mode, 4);
            buffer.put(data.getLength(), QRUtil.getLengthInBits(data.mode, typeNumber));
            data.write(buffer);
        }
        // calc num max data.
        let totalDataCount = 0;
        for (let i = 0; i < rsBlocks.length; i++) {
            totalDataCount += rsBlocks[i].dataCount;
        }
        if (buffer.getLengthInBits() > totalDataCount * 8) {
            throw new Error('code length overflow. (' +
                buffer.getLengthInBits() +
                '>' +
                totalDataCount * 8 +
                ')');
        }
        // end code
        if (buffer.getLengthInBits() + 4 <= totalDataCount * 8) {
            buffer.put(0, 4);
        }
        // padding
        while (buffer.getLengthInBits() % 8 != 0) {
            buffer.putBit(false);
        }
        // padding
        for (;;) {
            if (buffer.getLengthInBits() >= totalDataCount * 8) {
                break;
            }
            buffer.put(QRCode.PAD0, 8);
            if (buffer.getLengthInBits() >= totalDataCount * 8) {
                break;
            }
            buffer.put(QRCode.PAD1, 8);
        }
        return QRCode.createBytes(buffer, rsBlocks);
    }
    static createBytes(buffer, rsBlocks) {
        let offset = 0;
        let maxDcCount = 0;
        let maxEcCount = 0;
        const dcdata = new Array(rsBlocks.length);
        const ecdata = new Array(rsBlocks.length);
        for (let r = 0; r < rsBlocks.length; r++) {
            const dcCount = rsBlocks[r].dataCount;
            const ecCount = rsBlocks[r].totalCount - dcCount;
            maxDcCount = Math.max(maxDcCount, dcCount);
            maxEcCount = Math.max(maxEcCount, ecCount);
            dcdata[r] = new Array(dcCount);
            for (let i = 0; i < dcdata[r].length; i++) {
                dcdata[r][i] = 0xff & buffer.buffer[i + offset];
            }
            offset += dcCount;
            const rsPoly = QRUtil.getErrorCorrectPolynomial(ecCount);
            const rawPoly = new QRPolynomial(dcdata[r], rsPoly.getLength() - 1);
            const modPoly = rawPoly.mod(rsPoly);
            ecdata[r] = new Array(rsPoly.getLength() - 1);
            for (let i = 0; i < ecdata[r].length; i++) {
                const modIndex = i + modPoly.getLength() - ecdata[r].length;
                ecdata[r][i] = modIndex >= 0 ? modPoly.get(modIndex) : 0;
            }
        }
        let totalCodeCount = 0;
        for (let i = 0; i < rsBlocks.length; i++) {
            totalCodeCount += rsBlocks[i].totalCount;
        }
        const data = new Array(totalCodeCount);
        let index = 0;
        for (let i = 0; i < maxDcCount; i++) {
            for (let r = 0; r < rsBlocks.length; r++) {
                if (i < dcdata[r].length) {
                    data[index++] = dcdata[r][i];
                }
            }
        }
        for (let i = 0; i < maxEcCount; i++) {
            for (let r = 0; r < rsBlocks.length; r++) {
                if (i < ecdata[r].length) {
                    data[index++] = ecdata[r][i];
                }
            }
        }
        return data;
    }
}
QRCode.PAD0 = 0xec;
QRCode.PAD1 = 0x11;

/**
 * Generate QR SVG
 * @author Johan Nordberg <code@johan-nordberg.com>
 */
function generate(text, level = 'L', version = -1) {
    const qr = new QRCode(version, ErrorCorrectLevel[level]);
    const rects = [];
    qr.addData(text);
    qr.make();
    const rows = qr.modules;
    const size = rows.length;
    for (const [y, row] of rows.entries()) {
        let rect;
        for (const [x, on] of row.entries()) {
            if (on) {
                if (!rect)
                    rect = { x, y, width: 0, height: 1 };
                rect.width++;
            }
            else {
                if (rect && rect.width > 0) {
                    rects.push(rect);
                }
                rect = undefined;
            }
        }
        if (rect && rect.width > 0) {
            rects.push(rect);
        }
    }
    const svg = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}">`];
    for (const { x, y, width, height } of rects) {
        svg.push(`<rect x="${x}" y="${y}" width="${width}" height="${height}" />`);
    }
    svg.push('</svg>');
    return "data:image/svg+xml;base64," + btoa(svg.join(''));
}

class BrowserTransport {
    constructor(options = {}) {
        this.options = options;
        this.requestStatus = !(options.requestStatus === false);
        this.requestAccount = options.requestAccount || '';
        this.walletType = options.walletType || 'proton';
        this.storage = new Storage(options.storagePrefix || 'proton-link');
        this.showingManual = false;
    }
    closeModal() {
        this.hide();
        if (this.activeCancel) {
            this.activeRequest = undefined;
            this.activeCancel('');
            this.activeCancel = undefined;
        }
    }
    setupWidget() {
        this.showingManual = false;
        if (!this.Widget) {
            const widgetHolder = document.createElement('div');
            document.body.appendChild(widgetHolder);
            this.Widget = new Dialog({ target: widgetHolder });
            this.Widget.$on('back', () => document.dispatchEvent(new CustomEvent('backToSelector')));
            this.Widget.$on('close', () => this.closeModal());
        }
    }
    hide() {
        if (this.Widget) {
            this.Widget.$set({
                show: false,
                title: '',
                subtitle: null,
                showFootnote: false,
                countDown: null,
                qrData: null,
                action: null,
            });
        }
        this.clearTimers();
    }
    show() {
        if (this.Widget) {
            this.Widget.$set({ show: true });
        }
    }
    showDialog(args) {
        this.setupWidget();
        const props = {
            showBackButton: !args.hideBackButton,
            walletType: this.walletType,
            title: args.title || '',
            subtitle: args.subtitle || '',
            action: args.action || null,
            showFootnote: args.showFootnote,
            countDown: (args.content && args.content.countDown) || null,
            qrData: (args.content && args.content.qrData) || null,
        };
        if (this.Widget) {
            this.Widget.$set(Object.assign({}, props));
        }
        this.show();
    }
    displayRequest(request, title, subtitle = '', hideBackButton = false) {
        const sameDeviceRequest = request.clone();
        const returnUrl = generateReturnUrl();
        sameDeviceRequest.setInfoKey('same_device', true);
        sameDeviceRequest.setInfoKey('return_path', returnUrl);
        if (this.requestAccount.length > 0) {
            request.setInfoKey('req_account', this.requestAccount);
            sameDeviceRequest.setInfoKey('req_account', this.requestAccount);
        }
        const sameDeviceUri = sameDeviceRequest.encode(true, false);
        const crossDeviceUri = request.encode(true, false);
        const qrCode = generate(crossDeviceUri);
        const qrData = {
            code: qrCode,
            link: sameDeviceUri
        };
        this.showDialog({
            title,
            showFootnote: request.isIdentity(),
            subtitle,
            hideBackButton,
            content: { qrData },
        });
    }
    showLoading() {
        return __awaiter(this, void 0, void 0, function* () {
            this.showDialog({
                title: 'Pending...',
                subtitle: 'Preparing request...',
                hideBackButton: true,
                type: 'loading',
            });
        });
    }
    onRequest(request, cancel) {
        this.clearTimers();
        this.activeRequest = request;
        this.activeCancel = cancel;
        try {
            this.displayRequest(request, 'Scan the QR-Code');
        }
        catch (e) {
            cancel(e);
        }
    }
    onSessionRequest(session, request, cancel) {
        if (session.metadata.sameDevice) {
            request.setInfoKey('return_path', generateReturnUrl());
        }
        if (session.type === 'fallback') {
            this.onRequest(request, cancel);
            if (session.metadata.sameDevice) {
                // trigger directly on a fallback same-device session
                window.location.href = request.encode();
            }
            return;
        }
        this.activeRequest = request;
        this.activeCancel = cancel;
        const deviceName = session.metadata.name;
        // Countdown timer
        this.clearTimers();
        const timeout = session.metadata.timeout || 60 * 1000 * 2;
        const start = Date.now();
        const formatCountDown = (startTime) => {
            const secondsLeft = Math.floor((timeout + startTime - Date.now()) / 1000);
            const seconds = String(secondsLeft % 60).padStart(2, '0');
            const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
            return secondsLeft > 0 ? `${minutes}:${seconds}` : '00:00';
        };
        const updateCountdown = (startTime) => this.Widget && this.Widget.$set({ countDown: formatCountDown(startTime) });
        this.countdownTimer = setInterval(() => updateCountdown(start), 1000);
        updateCountdown(start);
        // Content subtitle
        this.showDialog({
            title: 'Signing Request',
            subtitle: `Please open ${deviceName || 'linked wallet'} to review the transaction`,
            content: {
                countDown: formatCountDown(start)
            },
            hideBackButton: true,
            action: {
                text: 'Optional: Sign manually using QR code',
                callback: () => {
                    this.clearTimers();
                    const error = new SessionError('Manual', 'E_TIMEOUT', session);
                    error[SkipToManual] = true;
                    cancel(error);
                },
            },
        });
        if (session.metadata.sameDevice && isMobile()) {
            window.location.href = `${request.getScheme()}://link`;
        }
    }
    sendSessionPayload(payload, session) {
        if (!session.metadata.triggerUrl || !session.metadata.sameDevice) {
            // not same device or no trigger url supported
            return false;
        }
        if (payload.array.length > 700) {
            // url could be clipped by iOS
            return false;
        }
        window.location.href = session.metadata.triggerUrl.replace('%s', Base64u.encode(payload.array));
        return true;
    }
    clearTimers() {
        if (this.closeTimer) {
            clearTimeout(this.closeTimer);
            this.closeTimer = undefined;
        }
        this.clearCountdown();
    }
    clearCountdown() {
        if (this.countdownTimer) {
            if (this.Widget) {
                this.Widget.$set({ countDown: undefined });
            }
            clearInterval(this.countdownTimer);
            this.countdownTimer = undefined;
        }
    }
    showRecovery(request, session) {
        request.data.info = request.data.info.filter(pair => pair.key !== 'return_path');
        if (session.type === 'channel') {
            const channelSession = session;
            if (channelSession.addLinkInfo) {
                channelSession.addLinkInfo(request);
            }
        }
        this.displayRequest(request, 'Sign manually', '', true);
        this.showingManual = true;
    }
    prepare(request, session) {
        return __awaiter(this, void 0, void 0, function* () {
            return request;
        });
    }
    recoverError(error, request) {
        if (!(request === this.activeRequest &&
            (error['code'] === 'E_DELIVERY' || error['code'] === 'E_TIMEOUT') &&
            error['session'])) {
            return false;
        }
        // recover from session errors by displaying a manual sign dialog
        if (this.showingManual) {
            // already showing recovery sign
            return true;
        }
        const session = error['session'];
        if (error[SkipToManual]) {
            this.showRecovery(request, session);
            return true;
        }
        this.clearCountdown();
        this.showDialog({
            title: 'Unable to reach device',
            subtitle: error.message || `Unable to deliver the request to ${session.metadata.name || 'the linked wallet'}.`,
            type: 'warning',
            action: {
                text: 'Optional: Sign manually using QR code',
                callback: () => this.showRecovery(request, session)
            },
        });
        return true;
    }
    onSuccess(request) {
        if (request === this.activeRequest) {
            this.clearTimers();
            this.hide();
        }
    }
    onFailure(request, error) {
        if (request !== this.activeRequest || error['code'] === 'E_CANCEL') {
            this.hide();
            return;
        }
        this.clearTimers();
        if (this.requestStatus) {
            this.showDialog({
                title: 'Transaction Error',
                subtitle: parseErrorMessage(error),
                hideBackButton: true,
                type: 'error',
            });
        }
        else {
            this.hide();
        }
    }
    userAgent() {
        return `BrowserTransport/${BrowserTransport.version} ${navigator.userAgent}`;
    }
}
/** Package version. */
BrowserTransport.version = '4.2.18'; // replaced by build script

export { BrowserTransport as default };
//# sourceMappingURL=proton-browser-transport.m.js.map
